// src/widget/v1/aliigo-widget.ts
import { extractOpeningQuestionSettings, resolveOpeningQuestions, type OpeningQuestionSettings } from "../../lib/widgetPromptSettings";
import { normalizeWidgetContext, registerWidgetEntry, dispatchWidgetProductSelection, WidgetActivationGate, type WidgetPageContext, type WidgetEntryOptions, type WidgetAskOptions, type WidgetEntryResult } from "../../lib/widgetEntry";
import { widgetDialogueIcon, widgetEntryStyles } from "../../lib/widgetEntryStyles";
import { normalizeLauncherBylineSettings, resolveLauncherByline } from "../../lib/widgetLauncherByline";
import { resolveWidgetPresentationText, type WidgetPresentationText } from "../../lib/widgetPresentationText";
import { stripLeakedLinkArtifacts } from "../../lib/runtime/linkCleanup";
import { sentenceSegments } from "../../lib/sentenceSegments";
import { safeProductUrl, normalizeProductDiscovery, type ProductCard, type ProductActionHandler, type ProductCartResult } from "../../lib/productCards";
import { requestsGuidedInput, guidedFieldHelp, normalizeGuidedBrief, type GuidedBrief } from "../../lib/widgetEntry";

type Role = "user" | "assistant";
type Action =
  | { type: "link"; label: string; url: string }
  | { type: "call"; label: string; phone: string }
  | { type: "handoff"; label: string; channels?: ("email"|"telegram"|"whatsapp")[] }
  | { type: "lead_form"; fields: ("name" | "email" | "phone")[]; reason?: string }
  | {
      type: "product_cards";
      presentation?: "inline_markers";
      products: ProductCard[];
    };

type Msg = { role: Role; content: string; actions?: Action[]; guidedInputRequested?: boolean };

type Theme = WidgetPresentationText & {
  headerBg?: string;   // "#111827 #ffffff"
  bubbleUser?: string; // "#2563eb #ffffff"
  bubbleBot?: string;  // "#f3f4f6 #111827"
  sendBg?: string;     // "#2563eb #ffffff"
  panelBg?: string;        // "#09090b"
  panelOpacity?: number;   // 0..1
  headerLogoUrl?: string;
  launcherLabel?: string;
  launcherByline?: string;
  launcherBylineByLocale?: Partial<Record<"en" | "es", string>>;
  headerTitle?: string;
};

type SessionPayload = {
  token: string;
  locale: "en" | "es";
  brand: string;
  launcher_label?: string;
  header_title?: string;
  slug: string;
  theme: Theme | null;
  show_branding?: boolean;
  show_header_icon?: boolean;
  show_widget?: boolean;
  locale_auto?: boolean;
  enabled_locales?: ("en" | "es")[];
  welcome_message_enabled?: boolean;
  welcome_messages?: Partial<Record<"en" | "es", string>>;
  welcome_fallback?: string;
  prompt_visibility?: Partial<OpeningQuestionSettings> & {
    showSuggestedPrompts?: boolean;
    showContextualTeaser?: boolean;
    teaserDelayMs?: number;
  };
  medical_disclaimer_enabled?: boolean;
  medical_disclaimer_text?: string;
  medical_disclaimer_url?: string;
  prompts?: {
    teaser?: string | null;
    prompts?: string[];
  };
};

type PromptMeta = {
  teaser: string | null;
  prompts: string[];
};

type WidgetState = {
  open: boolean;
  busy: boolean;
  isThinking: boolean;
  showSlowThinkingHint: boolean;
  conversationId: string | null;
  msgs: Msg[];
  session: SessionPayload | null;
  locale: "en" | "es";
  visitorSessionId: string | null;
  promptMeta: PromptMeta;
  quickReplies: string[];
  hasUserSentMessage: boolean;
  showTeaser: boolean;
  launcherMotionReady: boolean;
};

const WIDGET_VERSION = "v1";
const TEASER_TTL_MS = 7000;
const FALLBACK_PROMPTS_EN = [
  "How can you help me?",
  "What services do you offer?",
  "Can you guide me to the right option?",
];
const FALLBACK_PROMPTS_ES = [
  "¿Cómo puedes ayudarme?",
  "¿Qué servicios ofrecen?",
  "¿Puedes guiarme a la mejor opción?",
];

function createRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function inferPageType(pathname: string): string | undefined {
  const path = (pathname || "").toLowerCase();
  if (path === "/" || path === "") return "home";
  if (path.includes("/service")) return "service";
  if (path.includes("/treatment")) return "treatment";
  if (path.includes("/product")) return "product";
  if (path.includes("/location")) return "location";
  if (path.includes("/about")) return "about";
  if (path.includes("/pricing")) return "pricing";
  if (path.includes("/contact")) return "contact";
  if (path.includes("/faq")) return "faq";
  if (/(privacy|terms|legal|cookies|policy)/.test(path)) return "policy";
  return undefined;
}

function normalizePromptList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of input) {
    if (typeof item !== "string") continue;
    const clean = item.trim().replace(/\s+/g, " ");
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean.length > 90 ? `${clean.slice(0, 87).trimEnd()}...` : clean);
    if (out.length >= 4) break;
  }
  return out;
}

function mapServerActionsToWidget(actions: unknown): Action[] | undefined {
  if (!Array.isArray(actions)) return undefined;

  const out: Action[] = [];

  for (const a of actions) {
    if (!a || typeof a !== "object") continue;
    const obj = a as Record<string, unknown>;
    const t = obj.type;

    if (t === "cta") {
      const label = typeof obj.label === "string" ? obj.label : "";
      const url = typeof obj.url === "string" ? obj.url : "";
      if (label && url) out.push({ type: "link", label, url });
      continue;
    }

    if (t === "call") {
      const label = typeof obj.label === "string" ? obj.label : "";
      const phone = typeof obj.phone === "string" ? obj.phone : "";
      if (label && phone) out.push({ type: "call", label, phone });
      continue;
    }

    if (t === "collect_lead") {
      const fields = Array.isArray(obj.fields)
        ? obj.fields.filter((f) => f === "name" || f === "email" || f === "phone")
        : [];
      const reason = typeof obj.reason === "string" ? obj.reason : undefined;
      out.push({
        type: "lead_form",
        fields: fields.length ? (fields as ("name" | "email" | "phone")[]) : ["name", "email"],
        reason,
      });
      continue;
    }

    if (t === "product_cards") {
      const rawProducts = Array.isArray(obj.products) ? obj.products : [];
      const products = rawProducts
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const product = item as Record<string, unknown>;
          const name = typeof product.name === "string" ? product.name.trim() : "";
          const price = typeof product.price === "string" ? product.price.trim() : "";
          const image = typeof product.image === "string" ? product.image.trim() : "";
          const url = typeof product.url === "string" ? product.url.trim() : "";
          const description = typeof product.description === "string" ? product.description.trim() : "";
          const compareAt = typeof product.compareAt === "string" ? product.compareAt.trim() : "";
          const handle = typeof product.handle === "string" ? product.handle.trim().toLowerCase() : "";
          const discovery = product.commerce ? undefined : normalizeProductDiscovery(product.discovery);
          if (!name || (!price && !discovery) || !safeProductUrl(url) || (image && !safeProductUrl(image)) || !description) return null;
          const commerce = product.commerce && typeof product.commerce === "object" && !Array.isArray(product.commerce) ? product.commerce as Record<string, unknown> : null;
          const validatedCommerce = commerce?.provider === "shopify_storefront" && /^gid:\/\/shopify\/Product\/[0-9]+$/.test(String(commerce.productId)) &&
            (commerce.variantId === null || /^[0-9]+$/.test(String(commerce.variantId))) && typeof commerce.checkedAt === "string" && Number.isFinite(Date.parse(commerce.checkedAt)) &&
            Number.isSafeInteger(commerce.priceMinor) && Number(commerce.priceMinor) >= 0 && ["USD", "EUR", "GBP"].includes(String(commerce.currency))
            ? { provider: "shopify_storefront" as const, productId: String(commerce.productId), variantId: commerce.variantId === null ? null : String(commerce.variantId), checkedAt: commerce.checkedAt, priceMinor: Number(commerce.priceMinor), currency: String(commerce.currency) } : undefined;
          return {
            name,
            price,
            image,
            url,
            description,
            ...(handle ? { handle } : {}),
            ...(compareAt ? { compareAt } : {}),
            ...(validatedCommerce ? { commerce: validatedCommerce } : {}),
            ...(discovery && !validatedCommerce ? { discovery } : {}),
          };
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .slice(0, 3);
      const presentation = obj.presentation === "inline_markers" ? "inline_markers" : undefined;
      if (products.length > 0) {
        out.push({
          type: "product_cards",
          products,
          ...(presentation ? { presentation } : {}),
        });
      }
      continue;
    }
  }

  return out.length ? out : undefined;
}

function normalizeConversationErrorMessage(input: {
  locale: "en" | "es";
  status?: number;
  rawError?: string | null;
  genericMessage: string;
}): string {
  const raw = (input.rawError || "").trim();
  const lower = raw.toLowerCase();
  const quotaLike =
    input.status === 429 ||
    lower.includes("exceeded your current quota") ||
    lower.includes("insufficient_quota") ||
    lower.includes("billing") ||
    lower.includes("rate limit");

  const withCode = (message: string, code: string) =>
    `${message}\n\n${input.locale === "es" ? "Ref" : "Ref"}: ${code}`;

  if (quotaLike) {
    return withCode(
      input.locale === "es"
        ? "Ahora mismo no puedo responder automáticamente. Inténtalo de nuevo en un momento."
        : "I can’t reply automatically right now. Please try again in a moment.",
      "HEX5004",
    );
  }

  if (input.status === 401 || input.status === 403) {
    return withCode(
      input.locale === "es"
        ? "Ahora mismo no puedo responder automáticamente. Inténtalo de nuevo en un momento."
        : "I can’t reply automatically right now. Please try again in a moment.",
      "HEX5003",
    );
  }

  return withCode(raw || input.genericMessage, "HEX5000");
}


const UI = {
  en: {
    pill: (brand: string) => (brand || "Virtual assistance"),
    title: (brand: string) => (brand || "Virtual assistance"),
    welcome:
      "Hi — I’m here to help.\nI can answer questions or guide you to the right next step.",
    teaser: "Need help getting started?",
    suggestedLabel: "Try asking:",
    quickReplyLabel: "Would you like to explore:",
    placeholder: "Type your question…",
    send: "Send",
    errors: {
      sessionRefreshed: "Session refreshed. Please try again.",
      session: "Session error",
      tokenUnavailable: "Chat is temporarily unavailable.",
      tokenTroubleshootSupport:
        "We couldn't initialize a secure chat session. Refresh the page and try again.",
      tokenTroubleshootEmbed:
        "We couldn't start the assistant securely right now. Please try again in a moment.",
      network: "Network error",
      generic: "Something went wrong. Please try again.",
    },
    thinking: "Thinking...",
    lead: {
      title: "Share your details",
      name: "Name",
      email: "Email",
      phone: "Phone",
      submit: "Send details",
      sent: "Thanks! We’ll be in touch.",
      message: "Here are my contact details.",
      hiddenMessage: "Lead submitted.",
      followUp:
        "Thanks — we’ve received your information. We’ll follow up shortly.",
      consent: "I agree to be contacted about my request.",
      consentNote: "Your information will only be used to follow up on this request.",
    },
  },
  es: {
    pill: (brand: string) => (brand || "Asistencia virtual"),
    title: (brand: string) => (brand || "Asistencia virtual"),
    welcome:
      "Hola — estoy aquí para ayudarte.\nPuedo resolver tus dudas y orientarte hacia el siguiente paso.",
    teaser: "¿Necesitas ayuda para empezar?",
    suggestedLabel: "Prueba preguntando:",
    quickReplyLabel: "¿Quieres explorar:",
    placeholder: "Escribe tu consulta…",
    send: "Enviar",
    errors: {
      sessionRefreshed: "Sesión actualizada. Inténtalo de nuevo.",
      session: "Error de sesión",
      tokenUnavailable: "El chat no está disponible temporalmente.",
      tokenTroubleshootSupport:
        "No hemos podido iniciar una sesión segura del chat. Recarga la página e inténtalo de nuevo.",
      tokenTroubleshootEmbed:
        "No hemos podido iniciar el asistente de forma segura ahora mismo. Inténtalo de nuevo en un momento.",
      network: "Error de red",
      generic: "Algo salió mal. Inténtalo de nuevo.",
    },
    thinking: "Procesando...",
    lead: {
      title: "Déjanos tus datos",
      name: "Nombre",
      email: "Email",
      phone: "Teléfono",
      submit: "Enviar datos",
      sent: "¡Gracias! Te contactaremos.",
      message: "Aquí tienes mis datos de contacto.",
      hiddenMessage: "Datos enviados.",
      followUp:
        "Gracias — hemos recibido tu información. Te responderemos en breve.",
      consent: "Acepto que me contacten sobre mi solicitud.",
      consentNote: "Usaremos estos datos solo para dar seguimiento a tu solicitud.",
    },
  },
} as const;

function isEs(locale: string) {
  return (locale || "").toLowerCase().startsWith("es");
}

function normalizeEnabledLocales(input: unknown, fallback: "en" | "es"): ("en" | "es")[] {
  const out: ("en" | "es")[] = [];
  if (Array.isArray(input)) {
    for (const raw of input) {
      const locale = isEs(String(raw)) ? "es" : "en";
      if (!out.includes(locale)) out.push(locale);
    }
  }
  if (!out.length) out.push(fallback);
  if (!out.includes(fallback)) out.push(fallback);
  return out;
}

function splitPair(v?: string, defaults?: { bg: string; text: string }) {
  const s = (v || "").trim();
  const m = s.match(/#([0-9a-fA-F]{3}){1,2}/g) || [];
  const bg = m[0] || defaults?.bg || "#111827";
  const text = m[1] || defaults?.text || "#ffffff";
  return { bg, text };
}

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = (hex || "").trim().replace("#", "");
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function rgbaFromHex(hex: string, a: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamp01(a)})`;
}

function contrastTextFromHex(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#111827";
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance < 0.55 ? "#f4f4f5" : "#111827";
}

function escapeAttr(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}


class AliigoWidget extends HTMLElement {
  private root!: ShadowRoot;

  private STORAGE_TTL_MS = 12 * 60 * 1000;
  private STORAGE_PREFIX = "aliigo_widget_v1";

  private pendingScroll: "bottom" | "lastAssistantStart" | "lastUserStart" | "preserve" | null = null;
  private preRenderMessagesScrollTop: number | null = null;

  // TTL enforcement even without page refresh
  private expiryTimer: number | null = null;
  private lastActiveAtMs: number | null = null;

  private lastRenderOpen = false;
  private pendingFocus = false;
  private returnFocusTarget: HTMLElement | null = null;

  private sessionHydrated = false;
  private bootstrapped = false;
  private globalListenersBound = false;
  private sessionPromise: Promise<SessionPayload | null> | null = null;
  private identityGeneration = 0;
  private identityRequests = new Set<AbortController>();
  private cachedTheme: Theme | null = null;
  private cachedBrand = "";

  private onFocus = () => this.checkExpiryNow();
  private onVis = () => {
    if (!document.hidden) this.checkExpiryNow();
  };

  private telemetrySent = new Set<string>();
  private scrollLockY: number | null = null;

  private state: WidgetState = {
    open: false,
    busy: false,
    isThinking: false,
    showSlowThinkingHint: false,
    conversationId: null,
    msgs: [],
    session: null,
    locale: "en",
    visitorSessionId: null,
    promptMeta: {
      teaser: null,
      prompts: [],
    },
    quickReplies: [],
    hasUserSentMessage: false,
    showTeaser: false,
    launcherMotionReady: false,
  };
  private thinkingDelayTimer: number | null = null;
  private slowThinkingTimer: number | null = null;
  private teaserStartTimer: number | null = null;
  private teaserHideTimer: number | null = null;
  private launcherMotionTimer: number | null = null;

  static get observedAttributes() {
    return [
      "variant", "presentation", "guided-field-help", "embed-key", "api-base", "locale", "session-token",
      "floating-mode", "theme", "brand",
      "show-branding",
      "hide-header",
      "welcome-message-enabled",
      "welcome-messages",
      "welcome-fallback",
      "prompt-visibility",
      "launcher-visibility",
      "session-refresh-url",
      "start-open",
    ];
  }

  private ensureRoot() {
    if (!this.shadowRoot) {
      this.root = this.attachShadow({ mode: "open" });
    } else {
      this.root = this.shadowRoot;
    }
  }

  connectedCallback() {
    this.unregisterEntry?.();
    this.unregisterEntry = registerWidgetEntry(this, { open: (options) => this.openAssistant(options), ask: (options) => this.askQuestion(options), setContext: (context) => this.setPageContext(context), setProductActionHandler: (handler) => { this.productActionHandler = handler; this.render(); } });
    this.safeConnected();
  }

  private unregisterEntry?: () => void;
  private entryContext: WidgetPageContext | null = null;
  private entryQuestionsVisible = false;
  private entryPending = false;
  private activationGate = new WidgetActivationGate();
  private productActionHandler: ProductActionHandler | null = null;
  private productResults = new Map<string, ProductCartResult | { status: "pending" }>();
  private guidedBrief = { use: "", setting: "", appearance: "", requirements: "" };
  private guidedEditing = false;
  private guidedSubmitted: GuidedBrief | null = null;
  private guidedFollowUpOpen = false;
  private guidedDraft = "";

  private async addProduct(product: ProductCard) {
    const commerce = product.commerce, handler = this.productActionHandler;
    const generation = this.identityGeneration;
    if (!handler || !commerce?.variantId || this.productResults.get(commerce.productId)?.status === "pending" || this.productResults.get(commerce.productId)?.status === "unknown") return;
    if (new URL(product.url).origin !== window.location.origin) return;
    this.productResults.set(commerce.productId, { status: "pending" });
    this.savePersisted(false);
    this.render();
    let result: ProductCartResult;
    let timeout: number | undefined;
    try {
      result = await Promise.race([handler({ activationId: createRequestId(), productId: commerce.productId, variantId: commerce.variantId,
        quantity: 1, url: product.url, quotedPriceMinor: commerce.priceMinor, currency: commerce.currency }),
      new Promise<ProductCartResult>(resolve => { timeout = window.setTimeout(() => resolve({ status: "unknown" }), 12000); })]);
      if (!["added", "failed", "unavailable", "unknown"].includes(result?.status)) result = { status: "unknown" };
      if (result.cartUrl) {
        const url = new URL(result.cartUrl);
        if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}\/)?cart\/?$/.test(url.pathname) || url.username || url.password || url.search || url.hash) result = { status: "unknown" };
      }
    } catch { result = { status: "unknown" }; }
    finally { if (timeout !== undefined) window.clearTimeout(timeout); }
    if (generation !== this.identityGeneration) return;
    this.productResults.set(commerce.productId, result);
    this.render();
  }

  public setPageContext(context: WidgetPageContext | null): WidgetEntryResult {
    const normalized = normalizeWidgetContext(context, window.location.href);
    if (!normalized) return { status: "invalid_context" };
    this.entryContext = context === null ? null : normalized;
    this.entryQuestionsVisible = false;
    this.render();
    return { status: "context_updated" };
  }

  public async openAssistant(options: WidgetEntryOptions = {}): Promise<WidgetEntryResult> {
    const opener = document.activeElement;
    const generation = this.identityGeneration;
    await this.ensureSession();
    if (generation !== this.identityGeneration || !this.state.session?.token || this.state.session.show_widget === false || !this.isConnected) return { status: "unavailable" };
    if (options.locale && (!(this.state.session.enabled_locales ?? [this.state.locale]).includes(options.locale as "en" | "es") || !["en", "es"].includes(options.locale))) return { status: "invalid_locale" };
    if (options.context !== undefined) {
      if (this.setPageContext(options.context).status === "invalid_context") return { status: "invalid_context" };
    }
    if (options.locale) { this.setAttribute("locale", options.locale); this.setPresentationLocale(options.locale as "en" | "es"); }
    if (!this.state.open) {
      this.returnFocusTarget = opener instanceof HTMLElement && opener !== document.body && opener !== this ? opener : null;
    }
    this.entryQuestionsVisible = true;
    this.state.open = true;
    this.markTeaserSeen();
    this.pendingFocus = true;
    this.pendingScroll = this.state.msgs[this.state.msgs.length - 1]?.role === "assistant" ? "lastAssistantStart" : "lastUserStart";
    this.savePersisted(false);
    this.render();
    return { status: "opened" };
  }

  public async askQuestion(options: WidgetAskOptions): Promise<WidgetEntryResult> {
    if (!options || typeof options.questionId !== "string" || (options.activationId !== undefined && (typeof options.activationId !== "string" || !/^[a-zA-Z0-9_-]{1,128}$/.test(options.activationId)))) return { status: "invalid_question" };
    if (this.state.busy || this.entryPending) return { status: "busy" };
    this.entryPending = true;
    try {
      const generation = this.identityGeneration;
      await this.ensureSession();
      if (generation !== this.identityGeneration || !this.state.session?.token || this.state.session.show_widget === false || !this.isConnected) return { status: "unavailable" };
      const session = this.state.session;
      const locale = options.locale ?? this.state.locale;
      if (!["en", "es"].includes(locale) || !(session.enabled_locales ?? [this.state.locale]).includes(locale as "en" | "es")) return { status: "invalid_locale" };
      const context = options.context === undefined ? this.entryContext : normalizeWidgetContext(options.context, window.location.href);
      if (options.context !== undefined && !context) return { status: "invalid_context" };
      if (session?.prompt_visibility?.showSuggestedPrompts !== true) return { status: "invalid_question" };
      const opening = extractOpeningQuestionSettings({ widget: { prompts: session.prompt_visibility } });
      const question = resolveOpeningQuestions(opening, locale, context?.url ?? window.location.href).find((item) => item.id === options.questionId);
      if (!question) return { status: "invalid_question" };
      if (!this.activationGate.claim(question.id, options.activationId)) return { status: "duplicate" };
      const opened = await this.openAssistant(options);
      if (opened.status !== "opened") return opened;
      this.entryQuestionsVisible = false;
      await this.send(question.question);
      return { status: "submitted" };
    } finally { this.entryPending = false; }
  }

  private bindGlobalListeners() {
    if (this.globalListenersBound) return;
    window.addEventListener("focus", this.onFocus);
    document.addEventListener("visibilitychange", this.onVis);
    this.globalListenersBound = true;
  }

  private safeConnected() {
    try {
    this.ensureRoot();
    this.bindGlobalListeners();

    this.removeDuplicatePublicFloatingInstances();

    if (this.bootstrapped) {
      this.scheduleExpiryTimer();
      this.render();
      return;
    }
    this.bootstrapped = true;
    this.state.visitorSessionId = this.getOrCreateVisitorSessionId();

    // restore transcript + conversationId (if within TTL)
    this.loadPersisted();

    // If we have recent persisted messages, auto-open on refresh (floating only).
    // Keep the viewport aligned with the active/latest exchange start instead of
    // forcing bottom, which can show the tail of a long bubble first.
    if (this.getVariant() === "floating" && this.state.open && this.state.msgs.length > 0) {
      const lastRole = this.state.msgs[this.state.msgs.length - 1]?.role;
      this.pendingScroll = lastRole === "assistant" ? "lastAssistantStart" : "lastUserStart";
      this.pendingFocus = true;
    }

    // If client embed (fixed mode), move to <body> so it's truly viewport-fixed.
    if (
      this.getVariant() === "floating" &&
      this.getFloatingMode() === "fixed" &&
      !this.hasAttribute("data-no-teleport")
    ) {
      const host = document.body;
      if (this.parentElement !== host) host.appendChild(this);
    }

    if (this.getVariant() === "floating" && this.getStartOpen()) {
      this.state.open = true;
      this.pendingScroll = "bottom";
      this.pendingFocus = true;
    }

    // Enforce TTL even when the tab stays open
    this.scheduleExpiryTimer();

    this.sessionHydrated = false;
    this.state.showTeaser = false;
    this.render();

    const generation = this.identityGeneration;
    void this.ensureSession().finally(() => {
      if (generation !== this.identityGeneration) return;
      this.sessionHydrated = true;
      this.scheduleTeaserFromConfig();
      // If ensureSession already rendered an error (no session), don't overwrite it.
      if (this.state.session || !this.getEmbedKey()) this.render();
    });
    } catch (err) {
      this.reportTelemetry("connected_error", { message: this.errString(err) });
      this.renderError("Widget unavailable");
    }
  }

  disconnectedCallback() {
    this.unregisterEntry?.();
    window.removeEventListener("focus", this.onFocus);
    document.removeEventListener("visibilitychange", this.onVis);
    this.globalListenersBound = false;
    this.clearExpiryTimer();
    this.clearThinkingTimers();
    this.clearTeaserTimers();
    this.unlockPageScroll();
  }

  private clearThinkingTimers() {
    if (this.thinkingDelayTimer) {
      window.clearTimeout(this.thinkingDelayTimer);
      this.thinkingDelayTimer = null;
    }
    if (this.slowThinkingTimer) {
      window.clearTimeout(this.slowThinkingTimer);
      this.slowThinkingTimer = null;
    }
  }

  private clearTeaserTimers() {
    if (this.teaserStartTimer) {
      window.clearTimeout(this.teaserStartTimer);
      this.teaserStartTimer = null;
    }
    if (this.teaserHideTimer) {
      window.clearTimeout(this.teaserHideTimer);
      this.teaserHideTimer = null;
    }
    if (this.launcherMotionTimer) {
      window.clearTimeout(this.launcherMotionTimer);
      this.launcherMotionTimer = null;
    }
  }

  private beginThinkingWindow() {
    this.state.isThinking = false;
    this.state.showSlowThinkingHint = false;
    this.clearThinkingTimers();
    this.thinkingDelayTimer = window.setTimeout(() => {
      this.state.isThinking = true;
      this.state.showSlowThinkingHint = true;
      // Update the indicator in place so we do not rerender the messages list
      // (which can cause visible scroll jumps on long threads).
      this.refreshThinkingIndicatorDom();
    }, 2000);
  }

  private endThinkingWindow() {
    this.clearThinkingTimers();
    this.state.isThinking = false;
    this.state.showSlowThinkingHint = false;
    this.refreshThinkingIndicatorDom();
  }

  private refreshThinkingIndicatorDom() {
    const wrap = this.root.querySelector(".thinking-wrap") as HTMLDivElement | null;
    const hint = this.root.querySelector(".thinking-hint") as HTMLDivElement | null;
    if (!wrap || !hint) return;
    const visible = this.state.busy && this.state.isThinking;
    wrap.classList.toggle("visible", visible);
    hint.textContent = "";
  }

  private currentFallbackPrompts(locale: "en" | "es"): string[] {
    return locale === "es" ? [...FALLBACK_PROMPTS_ES] : [...FALLBACK_PROMPTS_EN];
  }

  private teaserSessionKey() {
    const embedKey = this.getEmbedKey() || this.state.session?.slug || "default";
    return `aliigo_teaser_seen:${embedKey}`;
  }

  private launcherMotionSessionKey() {
    const embedKey = this.getEmbedKey() || this.state.session?.slug || "default";
    return `aliigo_launcher_motion_seen:${embedKey}`;
  }

  private markTeaserSeen() {
    this.clearTeaserTimers();
    this.state.showTeaser = false;
    try {
      sessionStorage.setItem(this.teaserSessionKey(), "1");
    } catch {}
  }

  private shouldShowTeaser(): boolean {
    try {
      return sessionStorage.getItem(this.teaserSessionKey()) !== "1";
    } catch {
      return true;
    }
  }

  private shouldRunLauncherMotion(): boolean {
    try {
      return sessionStorage.getItem(this.launcherMotionSessionKey()) !== "1";
    } catch {
      return true;
    }
  }

  private markLauncherMotionSeen() {
    try {
      sessionStorage.setItem(this.launcherMotionSessionKey(), "1");
    } catch {}
  }

  private scheduleTeaserFade() {
    this.teaserHideTimer = window.setTimeout(() => {
      this.state.showTeaser = false;
      this.markTeaserSeen();
      this.render();
    }, TEASER_TTL_MS);
  }

  private scheduleTeaserFromConfig() {
    this.clearTeaserTimers();
    this.state.launcherMotionReady = false;
    if (this.getVariant() !== "floating") return;
    if (this.state.open) return;
    if (this.state.msgs.length > 0) return;
    const teaserAllowed = this.shouldShowTeaser();

    const session = this.state.session;
    const enabled = session?.prompt_visibility?.showContextualTeaser === true;
    if (!enabled || !teaserAllowed) {
      this.scheduleLauncherMotion();
      return;
    }
    const delay =
      typeof session?.prompt_visibility?.teaserDelayMs === "number"
        ? Math.max(0, Math.min(10000, Math.round(session.prompt_visibility.teaserDelayMs)))
        : 2000;

    this.teaserStartTimer = window.setTimeout(() => {
      this.state.launcherMotionReady = false;
      this.state.showTeaser = true;
      this.render();
      this.scheduleTeaserFade();
    }, delay);
    this.scheduleLauncherMotion();
  }

  private scheduleLauncherMotion() {
    if (!this.shouldRunLauncherMotion()) return;
    this.launcherMotionTimer = window.setTimeout(() => {
      if (this.state.showTeaser || this.state.open) return;
      this.state.launcherMotionReady = true;
      this.markLauncherMotionSeen();
      this.render();
      window.setTimeout(() => {
        this.state.launcherMotionReady = false;
        this.render();
      }, 1000);
    }, 3000);
  }

  private applyPromptMeta(rawMeta: unknown, mode: "session" | "response" = "response") {
    if (!rawMeta || typeof rawMeta !== "object") {
      if (mode === "response") this.state.quickReplies = [];
      return;
    }
    const meta = rawMeta as Record<string, unknown>;
    const promptsRaw = meta.prompts;
    if (!promptsRaw || typeof promptsRaw !== "object") {
      if (mode === "response") this.state.quickReplies = [];
      return;
    }
    const p = promptsRaw as Record<string, unknown>;
    const nextPrompts = normalizePromptList(p.prompts);
    const followUpSuggestions = meta.follow_up_suggestions === true;
    const teaser =
      typeof p.teaser === "string" && p.teaser.trim()
        ? p.teaser.trim()
        : UI[this.state.locale].teaser;
    this.state.promptMeta = {
      teaser: teaser || null,
      prompts: nextPrompts.length > 0 ? nextPrompts : this.currentFallbackPrompts(this.state.locale),
    };
    this.state.quickReplies =
      mode === "response" && followUpSuggestions ? this.state.promptMeta.prompts.slice(0, 4) : [];
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    this.safeAttributeChanged(name, oldValue, newValue);
  }

  private safeAttributeChanged(name: string, oldValue: string | null, newValue: string | null) {
    try {
    if (oldValue === newValue) return;
    // Attributes may arrive before connection (including clone/React mounting).
    // Bootstrap once all attributes exist and persisted history has been loaded.
    if (!this.isConnected) return;

    this.ensureRoot();
    // An embed key is stable across token renewal and presentation changes.
    // Token-only previews use the token as their identity; refreshable wrappers
    // retain history when renewing a token for the same business.
    if (name === "embed-key" || name === "api-base" ||
      (name === "session-token" && !this.getEmbedKey() && !this.getSessionRefreshUrlOverride())) {
      this.resetEmbedIdentity();
    }
    if (name === "locale") {
      this.setPresentationLocale(this.getLocaleOverride() || this.state.locale);
    }
    if (
      (name === "welcome-message-enabled" ||
        name === "welcome-messages" ||
        name === "welcome-fallback" ||
        name === "prompt-visibility")
    ) {
      this.syncSessionConfigOverridesFromAttributes();
      if (name === "prompt-visibility") {
        this.state.showTeaser = false;
        this.scheduleTeaserFromConfig();
      }
    }
    this.render();

    if (
      name === "embed-key" ||
      name === "api-base" ||
      name === "session-token"
    ) {
      this.state.session = null;

      // If the identity can change, reset cached skin + hydration
      if (name === "embed-key" || name === "session-token") {
        this.cachedTheme = null;
        this.cachedBrand = "";
        this.sessionHydrated = false;
      }

      const generation = this.identityGeneration;
      void this.ensureSession().finally(() => {
        if (generation !== this.identityGeneration) return;
        this.sessionHydrated = true;
        this.syncSessionConfigOverridesFromAttributes();
        // Same rule: don't overwrite renderError output.
        if (this.state.session || !this.getEmbedKey()) this.render();
      });
    }
    } catch (err) {
      this.reportTelemetry("attribute_changed_error", {
        name,
        message: this.errString(err),
      });
    }
  }

  private syncSessionConfigOverridesFromAttributes() {
    if (!this.state.session) {
      const overrideToken = this.getSessionTokenOverride();
      if (!overrideToken) return;

      const localeOverride = this.getLocaleOverride();
      const nextLocale = localeOverride || this.state.locale;
      const themeOverride = this.getThemeOverride();
      const brandOverride = this.getBrandOverride();

      this.state.session = {
        token: overrideToken,
        locale: nextLocale,
        brand: (brandOverride || "").trim(),
        launcher_label: (themeOverride?.launcherLabel || "").trim() || undefined,
        header_title: (themeOverride?.headerTitle || "").trim() || undefined,
        slug: "",
        theme: themeOverride,
        show_branding: this.getShowBrandingOverride() ?? false,
        show_header_icon: this.getShowHeaderIconOverride() ?? false,
        show_widget: true,
        locale_auto: false,
      };
      this.cachedTheme = this.state.session.theme || null;
      this.cachedBrand = this.state.session.brand || "";
      this.state.locale = nextLocale;
    }
    this.state.session = {
      ...this.state.session,
      welcome_message_enabled:
        this.getWelcomeMessageEnabledOverride() ?? this.state.session.welcome_message_enabled,
      welcome_messages: this.getWelcomeMessagesOverride() ?? this.state.session.welcome_messages,
      welcome_fallback: this.getWelcomeFallbackOverride() ?? this.state.session.welcome_fallback,
      prompt_visibility: this.getPromptVisibilityOverride() ?? this.state.session.prompt_visibility,
    };
  }


  private clearExpiryTimer() {
    if (this.expiryTimer != null) {
      window.clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private scheduleExpiryTimer() {
    this.clearExpiryTimer();

    // Only enforce TTL if we have a conversation transcript to expire
    if (!this.lastActiveAtMs || this.state.msgs.length === 0) return;

    const msLeft = this.lastActiveAtMs + this.STORAGE_TTL_MS - Date.now();
    if (msLeft <= 0) {
      this.expireConversation();
      return;
    }

    // Add a tiny buffer so we don't race exact millisecond boundaries
    this.expiryTimer = window.setTimeout(() => {
      this.checkExpiryNow();
    }, msLeft + 50);
  }

  private checkExpiryNow() {
    if (!this.lastActiveAtMs || this.state.msgs.length === 0) return;

    const idleMs = Date.now() - this.lastActiveAtMs;
    if (idleMs >= this.STORAGE_TTL_MS) {
      this.expireConversation();
    } else {
      this.scheduleExpiryTimer();
    }
  }

  private expireConversation() {
    // Clear persisted transcript and reset in-memory conversation
    this.clearPersisted();
    this.lastActiveAtMs = null;
    this.clearExpiryTimer();

    this.state.msgs = [];
    this.state.hasUserSentMessage = false;
    this.guidedSubmitted = null;
    this.guidedBrief = { use: "", setting: "", appearance: "", requirements: "" };
    this.guidedEditing = false;
    this.guidedFollowUpOpen = false;
    this.guidedDraft = "";
    this.state.conversationId = null;
    this.state.busy = false;
    this.endThinkingWindow();

    // For floating widgets, close on expiry
    if (this.getVariant() === "floating") {
      this.state.open = false;
    }

    this.render();
  }

  private applyPendingScroll() {
    const messages = this.root.querySelector(".messages") as HTMLDivElement | null;
    if (!messages || !this.pendingScroll) return;

    const mode = this.pendingScroll;
    this.pendingScroll = null;

    const scrollToBottom = () => {
      messages.scrollTop = messages.scrollHeight;
      requestAnimationFrame(() => {
        const max = Math.max(0, messages.scrollHeight - messages.clientHeight);
        messages.scrollTop = max;
      });
    };

    const scrollToLastAssistantStart = () => {
      const assistantBubbles = Array.from(
        messages.querySelectorAll('.row.bot[id^="msg-"] .bubble.bot'),
      ) as HTMLElement[];
      const lastBubble = assistantBubbles[assistantBubbles.length - 1];
      if (!lastBubble) {
        scrollToBottom();
        return;
      }

      const topPadding = 12;
      const bubbleTop = lastBubble.offsetTop;
      const max = Math.max(0, messages.scrollHeight - messages.clientHeight);
      messages.scrollTop = Math.max(0, Math.min(max, bubbleTop - topPadding));
    };
    const scrollToLastUserStart = () => {
      const userBubbles = Array.from(
        messages.querySelectorAll(".row.user .bubble.user"),
      ) as HTMLElement[];
      const lastBubble = userBubbles[userBubbles.length - 1];
      if (!lastBubble) {
        scrollToBottom();
        return;
      }

      const topPadding = 12;
      const bubbleTop = lastBubble.offsetTop;
      const max = Math.max(0, messages.scrollHeight - messages.clientHeight);
      messages.scrollTop = Math.max(0, Math.min(max, bubbleTop - topPadding));
    };
    const preserveScrollTop = () => {
      if (this.preRenderMessagesScrollTop == null) {
        scrollToBottom();
        return;
      }
      const max = Math.max(0, messages.scrollHeight - messages.clientHeight);
      messages.scrollTop = Math.max(0, Math.min(max, this.preRenderMessagesScrollTop));
    };
    const applyScrollNow = () => {
      if (mode === "lastAssistantStart") {
        scrollToLastAssistantStart();
        return;
      }
      if (mode === "lastUserStart") {
        scrollToLastUserStart();
        return;
      }
      if (mode === "preserve") {
        preserveScrollTop();
        return;
      }
      scrollToBottom();
    };

    // Apply immediately and reinforce over the next frames to absorb late
    // line-wrap / layout changes (especially in long messages).
    applyScrollNow();
    requestAnimationFrame(() => {
      applyScrollNow();
      requestAnimationFrame(() => {
        applyScrollNow();
      });
    });
  }


  private applyPendingFocus() {
    if (!this.pendingFocus) return;
    this.pendingFocus = false;

    requestAnimationFrame(() => {
      const input = this.root.querySelector(".input, .guided-input") as HTMLInputElement | HTMLTextAreaElement | null;
      input?.focus({ preventScroll: true });
    });
  }


  private getOrCreateVisitorSessionId() {
    const k = "aliigo_visitor_session_v1";
    try {
      const existing = localStorage.getItem(k);
      if (existing && existing.length >= 24) return existing;

      const rnd = crypto.getRandomValues(new Uint8Array(16));
      const id = Array.from(rnd).map((b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(k, id);
      return id;
    } catch {
      // last resort (no storage): per-page random
      return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  }

  private storageKey() {
    // embed-key is stable for real embeds; session-token covers dashboard preview
    const embedKey = this.getEmbedKey();
    const overrideToken = this.getSessionTokenOverride();
    const host = (window.location.hostname || "").toLowerCase();
    const key = embedKey || overrideToken || "no-key";
    return `${this.STORAGE_PREFIX}:${key}:${host}`;
  }

  private loadPersisted() {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;

      const parsed = JSON.parse(raw) as {
        savedAt?: number;
        lastActiveAt?: number;
        open?: boolean;
        conversationId?: string | null;
        msgs?: Msg[];
        locale?: "en" | "es";
        theme?: Theme | null;
        brand?: string;
        guidedSubmitted?: unknown;
        guidedBrief?: unknown;
      };

      if (parsed.theme) this.cachedTheme = parsed.theme;
      if (typeof parsed.brand === "string") this.cachedBrand = parsed.brand;

      const lastActive =
        typeof parsed.lastActiveAt === "number"
          ? parsed.lastActiveAt
          : typeof parsed.savedAt === "number"
            ? parsed.savedAt
            : 0;

      if (!lastActive || Date.now() - lastActive > this.STORAGE_TTL_MS) {
        this.clearPersisted();
        return;
      }

      // Restore the conversation, not a panel covering a newly navigated page.
      // Explicit start-open and contextual open calls retain their behavior.
      if (Array.isArray(parsed.msgs)) this.state.msgs = parsed.msgs;
      if (this.state.msgs.some((m) => m.role === "user")) this.state.hasUserSentMessage = true;
      if (this.state.hasUserSentMessage) {
        this.guidedSubmitted = normalizeGuidedBrief(parsed.guidedSubmitted);
        this.guidedBrief = normalizeGuidedBrief(parsed.guidedBrief) ?? this.guidedSubmitted ?? this.guidedBrief;
      }
      if (typeof parsed.conversationId === "string") this.state.conversationId = parsed.conversationId;
      if (parsed.locale === "en" || parsed.locale === "es") this.state.locale = parsed.locale;

      // Track last active for live TTL expiry
      if (this.state.msgs.length > 0) {
        this.lastActiveAtMs = lastActive || Date.now();
      }
    } catch {
      // ignore
    }
  }

  private savePersisted(touch: boolean = true) {
    try {
      const k = this.storageKey();
      const now = Date.now();

      // Preserve original savedAt if it exists
      let savedAt = now;

      // Preserve previous lastActiveAt if touch=false
      let prevLastActiveAt: number | undefined = undefined;

      try {
        const prevRaw = localStorage.getItem(k);
        if (prevRaw) {
          const prev = JSON.parse(prevRaw) as { savedAt?: number; lastActiveAt?: number };
          if (typeof prev.savedAt === "number") savedAt = prev.savedAt;
          if (typeof prev.lastActiveAt === "number") prevLastActiveAt = prev.lastActiveAt;
        }
      } catch {
        // ignore parse issues
      }

      const nextLastActiveAt = touch ? now : prevLastActiveAt;

      const payload = {
        savedAt,
        lastActiveAt: nextLastActiveAt,
        conversationId: this.state.conversationId,
        msgs: this.state.msgs,
        guidedSubmitted: this.guidedSubmitted,
        guidedBrief: this.guidedBrief,
        locale: this.state.locale,
        open: this.state.open,
        theme: this.cachedTheme,
        brand: this.cachedBrand,
      };

      localStorage.setItem(k, JSON.stringify(payload));

      // update live TTL tracking
      if (this.state.msgs.length > 0) {
        this.lastActiveAtMs = touch ? now : (nextLastActiveAt ?? this.lastActiveAtMs);
      } else {
        this.lastActiveAtMs = null;
      }

      this.scheduleExpiryTimer();
    } catch {
      // ignore storage failures
    }
  }

  private clearPersisted() {
    try {
      localStorage.removeItem(this.storageKey());
    } catch {}
  }

  private setPresentationLocale(nextLocale: "en" | "es") {
    if (this.state.locale === nextLocale) return;

    this.state.locale = nextLocale;
    this.state.promptMeta = { teaser: UI[nextLocale].teaser, prompts: [] };
    this.state.quickReplies = [];
    // A language change changes presentation, not conversation identity/history.
  }

  private resetEmbedIdentity() {
    this.guidedSubmitted = null;
    this.guidedFollowUpOpen = false;
    this.guidedDraft = "";
    this.guidedBrief = { use: "", setting: "", appearance: "", requirements: "" };
    this.guidedEditing = false;
    this.productActionHandler = null;
    this.productResults.clear();
    this.entryContext = null;
    this.entryQuestionsVisible = false;
    this.activationGate.clear();
    this.identityGeneration += 1;
    for (const request of this.identityRequests) request.abort();
    this.identityRequests.clear();
    this.sessionPromise = null;
    this.state.session = null;
    this.cachedTheme = null;
    this.cachedBrand = "";
    this.sessionHydrated = false;
    this.clearExpiryTimer();
    this.clearThinkingTimers();
    this.clearTeaserTimers();
    this.lastActiveAtMs = null;
    this.state.msgs = [];
    this.state.conversationId = null;
    this.state.hasUserSentMessage = false;
    this.state.quickReplies = [];
    this.state.promptMeta = { teaser: null, prompts: [] };
    this.state.busy = false;
    this.state.isThinking = false;
    this.state.showSlowThinkingHint = false;
    this.state.showTeaser = false;
    this.state.launcherMotionReady = false;
    this.pendingScroll = null;
    this.pendingFocus = false;
    // Leave the previous identity's persisted history under its own key.
    // The new session will persist only this fresh state under its new key.
  }

  private shouldLockPageScroll() {
    if (this.getVariant() !== "floating") return false;
    if (this.getFloatingMode() !== "fixed") return false;
    if (!this.state.open) return false;
    return window.matchMedia("(max-width: 900px)").matches;
  }

  private lockPageScroll() {
    if (this.scrollLockY != null) return;
    const body = document.body;
    const html = document.documentElement;
    this.scrollLockY = window.scrollY || window.pageYOffset || 0;
    body.style.position = "fixed";
    body.style.top = `-${this.scrollLockY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
  }

  private unlockPageScroll() {
    const body = document.body;
    const html = document.documentElement;
    const y = this.scrollLockY ?? 0;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    body.style.overflow = "";
    html.style.overscrollBehavior = "";
    if (this.scrollLockY != null) {
      window.scrollTo(0, y);
    }
    this.scrollLockY = null;
  }

  private getBrandOverride(): string | null {
    const b = (this.getAttribute("brand") || "").trim();
    return b || null;
  }

  private getVariant(): "floating" | "inline" | "hero" {
    const v = (this.getAttribute("variant") || "floating").toLowerCase();
    if (v === "inline" || v === "hero") return v;
    return "floating";
  }

  private isGuided() {
    return this.getVariant() === "inline" && this.getAttribute("presentation")?.trim().toLowerCase() === "guided";
  }

  private getEmbedKey() {
    return (this.getAttribute("embed-key") || "").trim();
  }

  private getApiBase() {
    return (this.getAttribute("api-base") || "https://aliigo.com").trim().replace(/\/$/, "");
  }

  private isPublicEmbedSessionRequest() {
    return Boolean(this.getEmbedKey()) && !this.getSessionTokenOverride() && !this.getSessionRefreshUrlOverride();
  }

  private isPublicFixedFloatingEmbed() {
    return (
      this.isPublicEmbedSessionRequest() &&
      this.getVariant() === "floating" &&
      this.getFloatingMode() === "fixed" &&
      !this.hasAttribute("data-no-teleport")
    );
  }

  private publicFloatingInstanceKey() {
    if (!this.isPublicFixedFloatingEmbed()) return null;
    return `${this.getApiBase()}::${this.getEmbedKey()}`;
  }

  private removeDuplicatePublicFloatingInstances() {
    const key = this.publicFloatingInstanceKey();
    if (!key) return;

    const nodes = Array.from(document.querySelectorAll("aliigo-widget")) as AliigoWidget[];
    for (const node of nodes) {
      if (node === this) continue;
      if (typeof node.publicFloatingInstanceKey !== "function") continue;
      if (node.publicFloatingInstanceKey() !== key) continue;
      try {
        if (node.isConnected) node.remove();
      } catch {
        // Duplicate cleanup must never affect the host page.
      }
    }
  }

  private hidePublicEmbed() {
    this.ensureRoot();
    this.root.innerHTML = "";
    this.style.display = "none";
  }

  private getLocaleOverride(): "en" | "es" | null {
    const v = (this.getAttribute("locale") || "").trim().toLowerCase();
    if (!v) return null;
    return v.startsWith("es") ? "es" : "en";
  }

  private detectWebsiteLocale(): "en" | "es" | null {
    try {
      const htmlLang = (document.documentElement.lang || "").toLowerCase();
      if (htmlLang.startsWith("es")) return "es";
      if (htmlLang.startsWith("en")) return "en";

      const og = (
        document
          .querySelector('meta[property="og:locale"],meta[name="og:locale"]')
          ?.getAttribute("content") || ""
      ).toLowerCase();
      if (og.startsWith("es")) return "es";
      if (og.startsWith("en")) return "en";

      const p = window.location.pathname.toLowerCase();
      if (/(^|\/)es(\/|$)/.test(p)) return "es";
      if (/(^|\/)en(\/|$)/.test(p)) return "en";

      // Heuristic fallback for sites where lang/path are not set correctly.
      const bodyText = (document.body?.innerText || "").toLowerCase().slice(0, 2500);
      if (bodyText) {
        const esHits =
          (bodyText.match(/\b(de|la|el|los|las|que|para|con|suscribete|libros|preguntas|ayuda)\b/g) || []).length;
        const enHits =
          (bodyText.match(/\b(the|and|for|with|your|books|questions|help|subscribe|about)\b/g) || []).length;
        if (esHits >= 3 && esHits > enHits * 1.25) return "es";
        if (enHits >= 3 && enHits > esHits * 1.25) return "en";
      }
    } catch {}
    return null;
  }

  private getSessionTokenOverride(): string | null {
    const t = (this.getAttribute("session-token") || "").trim();
    return t || null;
  }

  private getThemeOverride(): Theme | null {
    const raw = (this.getAttribute("theme") || "").trim();
    if (!raw) return null;

    try {
      const o = JSON.parse(raw) as Partial<Theme>;
      const out: Theme = {};

      if (typeof o.headerBg === "string") out.headerBg = o.headerBg;
      if (typeof o.bubbleUser === "string") out.bubbleUser = o.bubbleUser;
      if (typeof o.bubbleBot === "string") out.bubbleBot = o.bubbleBot;
      if (typeof o.sendBg === "string") out.sendBg = o.sendBg;
      if (typeof o.panelBg === "string") out.panelBg = o.panelBg;
      if (typeof o.panelOpacity === "number") out.panelOpacity = o.panelOpacity;
      if (typeof o.headerLogoUrl === "string") out.headerLogoUrl = o.headerLogoUrl;
      if (typeof o.launcherLabel === "string") out.launcherLabel = o.launcherLabel;
      Object.assign(out, normalizeLauncherBylineSettings(o) ?? {});
      if (typeof o.headerTitle === "string") out.headerTitle = o.headerTitle;
      for (const field of ["launcherLabel", "headerTitle"] as const) {
        const map = o[`${field}ByLocale`];
        if (map && typeof map === "object") {
          out[`${field}ByLocale`] = {};
          for (const locale of ["en", "es"] as const) {
            if (typeof map[locale] === "string") out[`${field}ByLocale`]![locale] = resolveWidgetPresentationText(o, locale, field);
          }
        }
      }


      return out;
    } catch {
      return null;
    }
  }

  private getWelcomeMessageEnabledOverride(): boolean | null {
    const v = (this.getAttribute("welcome-message-enabled") || "").trim().toLowerCase();
    if (!v) return null;
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  }

  private getWelcomeMessagesOverride(): Partial<Record<"en" | "es", string>> | null {
    const raw = (this.getAttribute("welcome-messages") || "").trim();
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const out: Partial<Record<"en" | "es", string>> = {};
      if (typeof parsed.en === "string" && parsed.en.trim()) out.en = parsed.en.trim().slice(0, 160);
      if (typeof parsed.es === "string" && parsed.es.trim()) out.es = parsed.es.trim().slice(0, 160);
      return out;
    } catch {
      return null;
    }
  }

  private getWelcomeFallbackOverride(): string | null {
    const raw = (this.getAttribute("welcome-fallback") || "").trim();
    return raw || null;
  }

  private getPromptVisibilityOverride():
    | (Partial<OpeningQuestionSettings> & { showSuggestedPrompts?: boolean; showContextualTeaser?: boolean; teaserDelayMs?: number })
    | null {
    const raw = (this.getAttribute("prompt-visibility") || "").trim();
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const out: NonNullable<SessionPayload["prompt_visibility"]> = { ...extractOpeningQuestionSettings({ widget: { prompts: parsed } }) };
      if (typeof parsed.showSuggestedPrompts === "boolean") out.showSuggestedPrompts = parsed.showSuggestedPrompts;
      if (typeof parsed.showContextualTeaser === "boolean") out.showContextualTeaser = parsed.showContextualTeaser;
      if (typeof parsed.teaserDelayMs === "number" && Number.isFinite(parsed.teaserDelayMs)) {
        out.teaserDelayMs = Math.max(0, Math.min(10000, Math.round(parsed.teaserDelayMs)));
      }
      return out;
    } catch {
      return null;
    }
  }

  private getSessionRefreshUrlOverride(): string | null {
    const raw = (this.getAttribute("session-refresh-url") || "").trim();
    return raw || null;
  }

  private getStartOpen(): boolean {
    return (this.getAttribute("start-open") || "").toLowerCase() === "true";
  }

  private getShowBrandingOverride(): boolean | null {
    const v = (this.getAttribute("show-branding") || "").trim().toLowerCase();
    if (!v) return null;
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  }

  private getShowHeaderIconOverride(): boolean | null {
    const v = (this.getAttribute("show-header-icon") || "").trim().toLowerCase();
    if (!v) return null;
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  }

  private getHideHeader(): boolean {
    return (this.getAttribute("hide-header") || "").toLowerCase() === "true";
  }

  private getFloatingMode(): "fixed" | "absolute" {
    const v = (this.getAttribute("floating-mode") || "").toLowerCase();
    return v === "absolute" ? "absolute" : "fixed";
  }

  private errString(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err ?? "unknown error");
  }

  private withTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
    const controller = new AbortController();
    this.identityRequests.add(controller);
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(input, { ...init, signal: controller.signal }).finally(() => {
      clearTimeout(timer);
      this.identityRequests.delete(controller);
    });
  }

  private reportTelemetry(event: string, detail?: Record<string, unknown>) {
    const key = `${event}:${JSON.stringify(detail || {})}`;
    if (this.telemetrySent.has(key)) return;
    this.telemetrySent.add(key);
    const apiBase = this.getApiBase();
    const embedKey = this.getEmbedKey();
    const payload = JSON.stringify({
      event,
      widgetVersion: WIDGET_VERSION,
      embedKeyHint: embedKey ? `${embedKey.slice(0, 6)}***` : null,
      pageHost: window.location.hostname || null,
      pagePath: window.location.pathname || null,
      userAgent: navigator.userAgent || null,
      ts: new Date().toISOString(),
      detail: detail || null,
    });
    const url = `${apiBase}/api/widget/telemetry`;
    try {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: payload,
        keepalive: true,
        credentials: "omit",
        mode: "cors",
      }).catch(() => undefined);
    } catch {
      // telemetry must never affect host page
    }
  }

  private async ensureSession(): Promise<SessionPayload | null> {
    if (this.sessionPromise) return this.sessionPromise;

    const overrideToken = this.getSessionTokenOverride();
    const embedKey = this.getEmbedKey();
    const refreshUrl = this.getSessionRefreshUrlOverride();
    if (!embedKey && !overrideToken && !refreshUrl) return null;

    // If we already have a session and it's still compatible with overrides, don’t thrash
    if (this.state.session && !overrideToken) return this.state.session;

    const generation = this.identityGeneration;
    this.sessionPromise = (async () => {
    try {
      const apiBase = this.getApiBase();

      if (overrideToken) {
        const localeOverride = this.getLocaleOverride();
        const nextLocale = localeOverride || this.state.locale;
        this.setPresentationLocale(nextLocale);
        const themeOverride = this.getThemeOverride();
        const brandOverride = this.getBrandOverride();
        const welcomeMessageEnabled = this.getWelcomeMessageEnabledOverride();
        const welcomeMessages = this.getWelcomeMessagesOverride();
        const welcomeFallback = this.getWelcomeFallbackOverride();
        const promptVisibility = this.getPromptVisibilityOverride();

        this.state.session = {
          token: overrideToken,
          locale: localeOverride || this.state.locale,
          brand: (brandOverride || "").trim(),
          launcher_label: (themeOverride?.launcherLabel || "").trim() || undefined,
          header_title: (themeOverride?.headerTitle || "").trim() || undefined,
          slug: "",
          theme: themeOverride,
          show_branding: this.getShowBrandingOverride() ?? false,
          show_header_icon: this.getShowHeaderIconOverride() ?? false,
          show_widget: true,
          locale_auto: false,
          welcome_message_enabled: welcomeMessageEnabled ?? false,
          welcome_messages: welcomeMessages ?? undefined,
          welcome_fallback: welcomeFallback ?? undefined,
          prompt_visibility: promptVisibility ?? undefined,
          medical_disclaimer_enabled: false,
        };

        this.cachedTheme = this.state.session.theme || null;
        this.cachedBrand = this.state.session.brand || "";
        this.state.locale = localeOverride || this.state.locale;
        this.savePersisted(false);
        this.render();
        return this.state.session;
      }

      if (!embedKey && refreshUrl) {
        const refreshed = await this.fetchRefreshedOverrideSession();
        if (generation !== this.identityGeneration) return null;
        if (!refreshed) {
          const localeOverride = this.getLocaleOverride() || this.state.locale;
          const t = UI[localeOverride];
          this.reportTelemetry("session_refresh_bootstrap_failed", {
            error: "no_token_from_refresh_url",
          });
          this.renderError(t.errors.session);
          return null;
        }
        return refreshed;
      }

      const url = new URL(`${apiBase}/api/embed/session`);
      url.searchParams.set("key", embedKey);
      url.searchParams.set("host", window.location.hostname);
      url.searchParams.set("path", window.location.pathname || "/");
      const requestedLocale = this.getAttribute("locale")?.trim().toLowerCase() ?? (this.detectWebsiteLocale() || this.state.locale || "en");
      url.searchParams.set("locale", requestedLocale);

      const res = await this.withTimeout(url.toString(), { method: "GET" }, 8000);
      const data = (await res.json().catch(() => ({}))) as Partial<SessionPayload> & { error?: string };
      if (generation !== this.identityGeneration) return null;

      if (!res.ok || !data.token) {
        this.state.session = null;
        const localeOverride = this.getLocaleOverride() || this.state.locale;
        const t = UI[localeOverride];
        const error = data.error || "no_token";
        this.reportTelemetry("session_fetch_failed", {
          status: res.status,
          error,
        });
        if (
          this.isPublicEmbedSessionRequest() &&
          res.status === 403 &&
          /domain not allowed/i.test(error)
        ) {
          this.hidePublicEmbed();
          return null;
        }
        this.renderError(error || t.errors.session);
        return null;
      }

      const localeOverride = this.getLocaleOverride();
      const fallbackLocale = isEs(data.locale || "en") ? "es" : "en";
      const localeAuto = Boolean(data.locale_auto);
      const detectedLocale = localeAuto ? this.detectWebsiteLocale() : null;
      const enabledLocales = normalizeEnabledLocales(data.enabled_locales, fallbackLocale);
      const candidateLocale = localeOverride || detectedLocale || fallbackLocale;
      const locale = enabledLocales.includes(candidateLocale) ? candidateLocale : fallbackLocale;
      this.setPresentationLocale(locale);

      this.state.session = {
        token: data.token,
        locale,
        brand: (data.brand || "").trim(),
        launcher_label:
          typeof (data as { launcher_label?: unknown }).launcher_label === "string"
            ? (((data as { launcher_label?: string }).launcher_label || "").trim() || undefined)
            : undefined,
        header_title:
          typeof (data as { header_title?: unknown }).header_title === "string"
            ? (((data as { header_title?: string }).header_title || "").trim() || undefined)
            : undefined,
        slug: (data.slug || "").trim(),
        theme: (data.theme as Theme | null) || null,
        show_branding: Boolean(data.show_branding),
        show_header_icon: Boolean((data as { show_header_icon?: boolean }).show_header_icon),
        show_widget: (data as { show_widget?: boolean }).show_widget !== false,
        locale_auto: localeAuto,
        enabled_locales: enabledLocales,
        welcome_message_enabled: (data as { welcome_message_enabled?: boolean }).welcome_message_enabled === true,
        welcome_messages:
          (data as { welcome_messages?: Partial<Record<"en" | "es", string>> }).welcome_messages || undefined,
        welcome_fallback:
          typeof (data as { welcome_fallback?: unknown }).welcome_fallback === "string"
            ? (((data as { welcome_fallback?: string }).welcome_fallback || "").trim() || undefined)
            : undefined,
        prompt_visibility:
          (data as {
            prompt_visibility?: Partial<OpeningQuestionSettings> & {
              showSuggestedPrompts?: boolean;
              showContextualTeaser?: boolean;
              teaserDelayMs?: number;
            };
          }).prompt_visibility || undefined,
        medical_disclaimer_enabled:
          (data as { medical_disclaimer_enabled?: boolean }).medical_disclaimer_enabled === true,
        medical_disclaimer_text:
          typeof (data as { medical_disclaimer_text?: unknown }).medical_disclaimer_text === "string"
            ? (((data as { medical_disclaimer_text?: string }).medical_disclaimer_text || "").trim() || undefined)
            : undefined,
        medical_disclaimer_url:
          typeof (data as { medical_disclaimer_url?: unknown }).medical_disclaimer_url === "string"
            ? (((data as { medical_disclaimer_url?: string }).medical_disclaimer_url || "").trim() || undefined)
            : undefined,
        prompts:
          (data as {
            prompts?: { teaser?: string | null; prompts?: string[] };
          }).prompts || undefined,
      };

      if (this.state.session.prompts) {
        this.applyPromptMeta({ prompts: this.state.session.prompts }, "session");
      }

      this.cachedTheme = this.state.session.theme || null;
      this.cachedBrand = this.state.session.brand || "";
      this.state.locale = locale;
      this.savePersisted(false);
      this.render();
      return this.state.session;
    } catch (err) {
      if (generation !== this.identityGeneration) return null;
      const localeOverride = this.getLocaleOverride() || this.state.locale;
      const t = UI[localeOverride];
      this.reportTelemetry("session_fetch_exception", { message: this.errString(err) });
      this.renderError(t.errors.network);
      return null;
    } finally {
      if (generation === this.identityGeneration) this.sessionPromise = null;
    }
    })();

    return this.sessionPromise;
  }

  private async fetchRefreshedOverrideSession(): Promise<SessionPayload | null> {
    const generation = this.identityGeneration;
    const refreshUrl = this.getSessionRefreshUrlOverride();
    if (!refreshUrl) return null;

    const res = await this.withTimeout(refreshUrl, { method: "GET", cache: "no-store" }, 8000);
    const data = (await res.json().catch(() => ({}))) as Partial<SessionPayload> & { error?: string };
    if (generation !== this.identityGeneration) return null;
    if (!res.ok || !data.token) return null;

    const localeOverride = this.getLocaleOverride();
    const fallbackLocale = isEs(data.locale || "en") ? "es" : "en";
    const locale = localeOverride || fallbackLocale;

    const nextSession: SessionPayload = {
      token: data.token,
      locale,
      brand: (data.brand || "").trim(),
      launcher_label:
        typeof (data as { launcher_label?: unknown }).launcher_label === "string"
          ? (((data as { launcher_label?: string }).launcher_label || "").trim() || undefined)
          : undefined,
      header_title:
        typeof (data as { header_title?: unknown }).header_title === "string"
          ? (((data as { header_title?: string }).header_title || "").trim() || undefined)
          : undefined,
      slug: (data.slug || "").trim(),
      theme: (data.theme as Theme | null) || null,
      show_branding: Boolean(data.show_branding),
      show_header_icon: Boolean((data as { show_header_icon?: boolean }).show_header_icon),
      show_widget: (data as { show_widget?: boolean }).show_widget !== false,
      locale_auto: false,
      welcome_message_enabled: (data as { welcome_message_enabled?: boolean }).welcome_message_enabled === true,
      welcome_messages:
        (data as { welcome_messages?: Partial<Record<"en" | "es", string>> }).welcome_messages || undefined,
      welcome_fallback:
        typeof (data as { welcome_fallback?: unknown }).welcome_fallback === "string"
          ? (((data as { welcome_fallback?: string }).welcome_fallback || "").trim() || undefined)
          : undefined,
      prompt_visibility:
        (data as {
          prompt_visibility?: Partial<OpeningQuestionSettings> & {
            showSuggestedPrompts?: boolean;
            showContextualTeaser?: boolean;
            teaserDelayMs?: number;
          };
        }).prompt_visibility || undefined,
      medical_disclaimer_enabled:
        (data as { medical_disclaimer_enabled?: boolean }).medical_disclaimer_enabled === true,
      medical_disclaimer_text:
        typeof (data as { medical_disclaimer_text?: unknown }).medical_disclaimer_text === "string"
          ? (((data as { medical_disclaimer_text?: string }).medical_disclaimer_text || "").trim() || undefined)
          : undefined,
      medical_disclaimer_url:
        typeof (data as { medical_disclaimer_url?: unknown }).medical_disclaimer_url === "string"
          ? (((data as { medical_disclaimer_url?: string }).medical_disclaimer_url || "").trim() || undefined)
          : undefined,
      prompts:
        (data as {
          prompts?: { teaser?: string | null; prompts?: string[] };
        }).prompts || undefined,
    };

    this.state.session = nextSession;
    this.cachedTheme = nextSession.theme || null;
    this.cachedBrand = nextSession.brand || "";
    this.state.locale = locale;
    this.setAttribute("session-token", nextSession.token);
    if (nextSession.welcome_fallback) {
      this.setAttribute("welcome-fallback", nextSession.welcome_fallback);
    } else {
      this.removeAttribute("welcome-fallback");
    }
    this.savePersisted(false);
    this.render();
    return nextSession;
  }

  private async tryRefreshSessionToken(): Promise<string | null> {
    if (this.getSessionTokenOverride() && this.getSessionRefreshUrlOverride()) {
      const generation = this.identityGeneration;
      try {
        const refreshed = await this.fetchRefreshedOverrideSession();
        return refreshed?.token ?? null;
      } catch (err) {
        if (generation !== this.identityGeneration) return null;
        throw err;
      }
    }
    this.state.session = null;
    const s = await this.ensureSession();
    return s?.token ?? null;
  }

  private async refreshSessionIfStale() {
    // Enforce chat TTL before sending
    this.checkExpiryNow();

    const idleMs = this.lastActiveAtMs ? Date.now() - this.lastActiveAtMs : 0;
    const refreshThresholdMs = 25 * 60 * 1000;

    if (idleMs >= refreshThresholdMs || !this.state.session?.token) {
      await this.tryRefreshSessionToken();
    }
  }

  private renderError(msg: string) {
    this.ensureRoot();
    const locale = this.getLocaleOverride() || this.state.locale;
    const t = UI[locale];
    if (this.isGuided()) {
      const title = resolveWidgetPresentationText(this.getThemeOverride() || this.cachedTheme || {}, locale, "headerTitle") || this.getBrandOverride() || "Aliigo";
      this.root.innerHTML = `<style>${this.css()}</style><div class="wrap inline"><section class="panel inline guided" style="--guided-text:#111827"><div class="header">${escapeHtml(title)}</div><div class="guided-results" role="alert">${escapeHtml(msg || t.errors.session)}</div></section></div>`;
      return;
    }
    this.root.innerHTML = `
      <style>${this.css()}</style>
      <div class="wrap inline">
        <div class="panel">
          <div class="header">Aliigo</div>
          <div class="body"><div class="bubble bot">${msg || t.errors.session}</div></div>
        </div>
      </div>
    `;
  }

  private css() {
    return `
      :host { all: initial; display: block; }
      :host([variant="inline"]),
      :host([variant="hero"]) {
        display: block;
        width: 100%;
        min-width: 0;
      }
      :host([variant="hero"]) {
        min-height: 560px;
      }
      :host([floating-mode="fixed"]) { position: relative; z-index: 2147483647; }
      :host([floating-mode="absolute"]) { position: relative; width: 100%; height: 100%; display: block; pointer-events: none; }

      .wrap {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
      }
      .wrap * { box-sizing: border-box; }

      /* --- Positioning --- */
      .floating.fixed {
        position: fixed;
        right: 20px;
        bottom: 20px;
        z-index: 2147483647;
        pointer-events: none;
      }
      .floating.fixed .pill,
      .floating.fixed .panel {
        pointer-events: auto;
      }
      @media (max-width: 640px) {
        .floating.fixed {
          right: 16px;
          left: auto;
          bottom: 16px;
        }
      }
      .floating.absolute {
        position: absolute;
        inset: 0;
        right: 0;
        z-index: 99999;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
        padding: 14px 0 24px 14px;
        pointer-events: none;
      }
      .floating.absolute .pill,
      .floating.absolute .panel {
        pointer-events: auto;
      }
      .floating.absolute .panel {
        max-width: 100%;
        max-height: 100%;
      }
      @media (max-width: 640px) {
        .floating.absolute {
          right: 0;
          padding: 12px;
        }
      }
      .inline { width: 100%; }
      .hero { width: 100%; min-height: 560px; height: 100%; max-width: 100%; margin: 0 auto; }

      /* --- Pill (Launcher) --- */
      .pill {
        border: 0; cursor: pointer; font-weight: 600;
        border-radius: 2px 18px 2px 18px; padding: 14px 28px;
        min-width: 160px;
        box-shadow: 0 8px 20px rgba(15,23,42,0.22), 0 2px 6px rgba(15,23,42,0.16);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        font-size: 15px;
        display: flex; align-items: center; justify-content: center; gap: 8px;
      }
      .pill:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(15,23,42,0.28), 0 3px 8px rgba(15,23,42,0.18); }
      .pill:active { transform: translateY(0); }
      .pill.attn-once {
        animation: launcher-attn 900ms cubic-bezier(0.16, 1, 0.3, 1) 1;
      }
      .launcher-wrap {
        position: relative;
        display: inline-flex;
        justify-content: flex-end;
        align-items: center;
      }
      .teaser {
        position: absolute;
        right: 0;
        bottom: 60px;
        min-width: 220px;
        max-width: 320px;
        padding: 14px 28px;
        border-radius: 2px 18px 2px 18px;
        border: 1px solid rgba(0,0,0,0.12);
        box-shadow: 0 8px 20px rgba(15,23,42,0.22);
        font-size: 14px;
        font-weight: 700;
        line-height: 1.35;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        pointer-events: auto;
        cursor: pointer;
        animation: teaser-fade-in 220ms ease-out;
      }

      .suggest-wrap { margin-top: 10px; margin-bottom: 12px; }
      .suggest-label {
        font-size: 12px;
        color: #6b7280;
        margin-bottom: 8px;
      }
      .suggest-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .suggest-chip {
        border: 1px solid var(--prompt-border, rgba(0,0,0,0.12));
        background: var(--prompt-bg, rgba(37,99,235,0.08));
        color: var(--prompt-text, #111827);
        border-radius: 18px;
        padding: 10px 14px;
        font-size: 13px;
        line-height: 1.4;
        cursor: pointer;
        max-width: 100%;
        min-height: 44px;
        white-space: normal;
        overflow-wrap: anywhere;
        font-family: inherit;
        display: inline-flex;
        align-items: center;
        text-align: left;
      }
      .suggest-chip:hover {
        border-color: var(--prompt-border, rgba(0,0,0,0.2));
        background: var(--prompt-hover, rgba(37,99,235,0.15));
      }
      .suggest-chip:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
      .suggest-chip:disabled { opacity: 0.55; cursor: default; }
      .suggest-chip:active { transform: scale(0.98); }

      /* --- Main Panel --- */
      .panel {
        width: 380px;
        height: 600px;
        max-height: 80vh;

        background: var(--panel-bg, #ffffff); /* NEW */
        border: 1px solid rgba(0,0,0,0.06);
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12);
        display: flex;
        flex-direction: column;
        isolation: isolate;
      }

      .panel.animate-in {
      animation: panel-enter 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      }

      .panel.inline { width: 100%; max-width: 100%; height: 500px; box-shadow: none; border: 1px solid #e5e7eb; }
      .panel.hero {
        width: 100%;
        max-width: 100%;
        min-height: 560px;
        height: 100%;
        max-height: none;
        border-radius: 18px;
        border: 1px solid rgba(15,23,42,0.10);
        box-shadow: 0 18px 50px rgba(15,23,42,0.14);
      }
      .panel.no-header .messages { padding-top: 24px; }

      /* --- Header --- */
      .header {
        padding: 16px 20px;
        position: relative;
        overflow: visible;
        display: flex; align-items: center; justify-content: space-between;
        border-bottom: 1px solid rgba(0,0,0,0.05);
        font-weight: 600;
        font-size: 16px;
        letter-spacing: -0.01em;
      }
      .header-main {
        flex: 1;
        min-width: 0;
        display: inline-flex;
        align-items: center;
        gap: 10px;
      }
      .header-icon {
        width: 34px;
        height: 34px;
        border-radius: 4px;
        object-fit: contain;
        display: block;
        flex: 0 0 auto;
        pointer-events: none;
      }
      .header-title {
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .close {
        border: 0; background: transparent; cursor: pointer;
        width: 44px; height: 44px;
        display: inline-flex; align-items: center; justify-content: center;
        flex: 0 0 auto;
        font-size: 26px; line-height: 1; opacity: 0.72;
        padding: 0; margin: -10px -12px -10px 8px;
        position: relative; z-index: 2147483647;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        transition: opacity 0.2s;
      }
      .close:hover { opacity: 1; }

      /* --- Body & Messages --- */
      .body {
        flex: 1;
        min-height: 0;
        position: relative;
        background: transparent;
        display: flex;
        flex-direction: column;
      }
      .messages {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        touch-action: pan-y;
        padding: 20px 20px calc(var(--composer-safe, 128px) + env(safe-area-inset-bottom, 0px));
      }

      .row {
        margin-top: 12px;
        display: flex;
        width: 100%;
      }
      .row.user { justify-content: flex-end; }
      .row.bot { justify-content: flex-start; }

      /* --- Bubbles --- */
      .bubble {
        display: block;
        position: relative;
        max-width: 85%;
        padding: 10px 16px;
        border-radius: 18px;
        font-size: 15px;
        line-height: 1.5;
        word-wrap: break-word;
        white-space: normal;
        box-shadow: 0 1px 2px rgba(0,0,0,0.04);
        transform-origin: bottom center;
      }

      .bubble.anim {
        animation: message-enter 0.3s cubic-bezier(0.25, 1, 0.5, 1) forwards;
      }

      .bubble.user {
        border-bottom-right-radius: 4px;
        margin-left: 40px;
      }

      .bubble.bot {
        border-bottom-left-radius: 4px;
        margin-right: 40px;
        border: 1px solid rgba(0,0,0,0.04);
      }

      .bubble strong { font-weight: 600; }
      .bubble .para { margin: 0; }
      .bubble .para + .para { margin-top: 14px; }
      .bubble .lead { margin: 0 0 10px 0; }
      .bubble .tail { margin: 10px 0 0 0; }
      .bubble ol.list { list-style: decimal outside; }
      .bubble ul.list { list-style: disc outside; }
      .bubble .list { margin: 8px 0 0 0; padding-left: 18px; }
      .bubble .list li { margin: 4px 0; padding-left: 2px; }

      /* --- Input Area --- */
      .form {
        flex: 0 0 auto;
        display: flex;
        gap: 10px;
        padding: 16px;
        border-top: 1px solid rgba(0,0,0,0.06);
        background: var(--panel-bg, #ffffff);
        position: relative;
        z-index: 2;
      }

      .powered-wrap {
        flex: 0 0 auto;
        border-top: 1px solid rgba(255,255,255,0.14);
        background: #0b0b0c;
        padding: 10px 12px 8px;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 7px;
        position: relative;
        z-index: 2;
      }
      .medical-disclaimer {
        color: #d4d4d8;
        font-size: 10.5px;
        line-height: 1.35;
        text-align: left;
      }
      .medical-disclaimer.with-branding {
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255,255,255,0.16);
      }
      .medical-disclaimer a {
        color: #ffffff;
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      .powered-brand-row {
        display: flex;
        justify-content: flex-end;
      }
      .powered-link {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        text-decoration: none;
        color: #ffffff;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.01em;
      }
      .powered-prefix {
        color: #d4d4d8;
        font-weight: 500;
        font-style: italic;
      }
      .powered-logo {
        width: 64px;
        height: 21px;
        object-fit: contain;
        display: inline-block;
      }

      /* --- Actions (buttons/links under assistant messages) --- */
      .actions { margin-top: 8px; display:flex; flex-wrap:wrap; gap:8px; }
      .action, .action-btn {
        display:inline-flex; align-items:center; justify-content:center;
        padding:10px 12px; border-radius:12px; font-weight:600; font-size:14px;
        border:1px solid var(--action-border, rgba(0,0,0,0.10));
        background:var(--action-bg, #fff); color:var(--action-text, #111827);
        text-decoration:none; cursor:pointer;
        box-shadow: 0 8px 20px var(--action-shadow, rgba(0,0,0,0.08));
        transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;
      }
      .action:hover, .action-btn:hover {
        border-color: var(--action-border-hover, var(--action-border, rgba(0,0,0,0.18)));
        filter: brightness(0.96);
        transform: translateY(-1px);
      }
      .product-cards {
        margin-top: 10px;
        display: grid;
        gap: 10px;
        width: 100%;
      }
      .bubble.with-products { max-width: 100%; width: 100%; margin-right: 0; padding: 10px; box-sizing: border-box; }
      .product-cards.inline {
        margin: 8px 0 12px;
      }
      .product-card {
        display: grid;
        grid-template-columns: 86px minmax(0, 1fr);
        gap: 10px;
        border: 1px solid rgba(0,0,0,0.10);
        background: rgba(255,255,255,0.92);
        border-radius: 14px;
        padding: 9px;
        color: #111827;
        text-decoration: none;
        box-shadow: 0 8px 20px rgba(15,23,42,0.08);
      }
      .product-card:hover { border-color: var(--prompt-border, rgba(0,0,0,0.18)); }
      .product-name { overflow-wrap: anywhere; }
      .product-operation { font-size: 11px; line-height: 1.4; }
      .product-actions {
        margin-top: auto;
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      }
      .product-image-wrap {
        height: 104px;
        border-radius: 11px;
        background: #ffffff;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .product-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 6px;
        display: block;
      }
      .product-no-image { font-size: 11px; color: #6b7280; text-align: center; }
      .product-info { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
      .product-name { font-size: 13px; line-height: 1.25; font-weight: 700; color: #111827; }
      .product-price-row { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
      .product-price { font-size: 13px; font-weight: 800; color: #24785f; }
      .product-compare { font-size: 11px; color: #9ca3af; text-decoration: line-through; }
      .product-description {
        font-size: 11px;
        line-height: 1.35;
        color: #4b5563;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .product-cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        min-height: 28px;
        border: 0;
        border-radius: 999px;
        background: var(--action-bg, #84c9ad);
        color: var(--action-text, #111827);
        padding: 5px 8px;
        font-size: 10px;
        line-height: 1;
        font-weight: 800;
        white-space: nowrap;
        cursor: pointer;
      }
      .product-cta.secondary {
        border: 1px solid rgba(0,0,0,0.12);
        background: #ffffff;
        color: #24785f;
      }
      .product-cta:disabled { opacity: 0.6; cursor: default; }
      .product-cta:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
      @media (max-width: 480px) {
        .product-card { grid-template-columns: 68px minmax(0, 1fr); gap: 8px; }
        .product-cta { min-height: 44px; font-size: 12px; white-space: normal; }
      }
      .source-foot {
        margin-top: 10px;
        padding: 8px 10px;
        border-radius: 10px;
        background: rgba(0, 0, 0, 0.08);
        font-size: 12px;
        line-height: 1.4;
      }

      .lead-form {
        margin-top: 8px;
        display: grid;
        gap: 8px;
        padding: 10px;
        border-radius: 12px;
        border: 1px solid rgba(0,0,0,0.08);
        background: rgba(255,255,255,0.75);
      }
      .lead-field { display: grid; gap: 4px; }
      .lead-label { font-size: 12px; color:#6b7280; }
      .lead-input {
        height: 36px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        border-radius: 10px;
        padding: 0 10px;
        font-size: 14px;
        color: #111827;
        outline: none;
      }
      .lead-input:focus { border-color:#d1d5db; box-shadow: 0 0 0 3px rgba(0,0,0,0.05); }
      .lead-submit {
        height: 36px;
        border: 0;
        border-radius: 10px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        background: #111827;
        color: #ffffff;
      }
      .lead-consent {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        font-size: 12px;
        color: #374151;
      }
      .lead-consent input {
        margin-top: 2px;
      }
      .lead-consent-note {
        font-size: 11px;
        color: #6b7280;
        margin-top: -2px;
      }
      .lead-form.sent .lead-input,
      .lead-form.sent .lead-submit {
        opacity: 0.6;
        pointer-events: none;
      }


      .input {
        flex: 1;
        min-width: 0;
        height: 44px;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        border-radius: 22px;
        padding: 0 16px;
        font-size: 15px;
        outline: none;
        transition: all 0.2s ease;
        color: #111827;
      }
      .input:focus {
        background: #ffffff;
        border-color: #d1d5db;
        box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
      }
      .input::placeholder { color: #9ca3af; }

      .send {
        height: 44px;
        padding: 0 20px;
        border: 0;
        border-radius: 22px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        flex-shrink: 0;
        transition: transform 0.1s ease, opacity 0.2s;
      }
      .send:active { transform: scale(0.96); }
      .send:disabled { opacity: 0.5; cursor: not-allowed; filter: grayscale(1); }
      .token-help {
        margin: 0 12px 12px;
        padding: 10px 12px;
        border-radius: 12px;
        background: rgba(254, 242, 242, 0.96);
        border: 1px solid rgba(220, 38, 38, 0.15);
        color: #7f1d1d;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .token-help-title {
        font-size: 12px;
        font-weight: 700;
      }
      .token-help-body {
        font-size: 12px;
        line-height: 1.45;
      }

      .thinking-wrap {
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 10px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 8px;
        opacity: 0;
        pointer-events: none;
        transform: translateY(4px);
        transition: opacity 0.18s ease, transform 0.18s ease;
        z-index: 3;
      }
      .thinking-wrap.visible {
        opacity: 1;
        transform: translateY(0);
      }
      .thinking-hint {
        display: none;
        font-size: 11px;
        color: #6b7280;
        opacity: 0;
      }
      .thinking-wrap.visible .thinking-hint {
        opacity: 1;
      }
      .typing {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-radius: 12px;
        border: 1px solid rgba(0,0,0,0.08);
      }
      .typing-dot {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        opacity: 0.45;
        animation: aliigoTypingPulse 1.1s infinite ease-in-out;
      }
      .typing-dot:nth-child(2) { animation-delay: 0.14s; }
      .typing-dot:nth-child(3) { animation-delay: 0.28s; }
      @keyframes aliigoTypingPulse {
        0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
        40% { transform: translateY(-2px); opacity: 1; }
      }

      @keyframes message-enter {
        0% { opacity: 0; transform: translateY(10px) scale(0.98); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes panel-enter {
        0% { opacity: 0; transform: translateY(20px) scale(0.96); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes teaser-fade-in {
        0% { opacity: 0; transform: translateY(6px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes launcher-attn {
        0% { transform: translateY(6px); box-shadow: 0 4px 12px rgba(15,23,42,0.12); }
        60% { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(15,23,42,0.26); }
        100% { transform: translateY(0); box-shadow: 0 8px 20px rgba(15,23,42,0.22), 0 2px 6px rgba(15,23,42,0.16); }
      }

      @media (max-width: 640px) {
        .floating.absolute {
          padding: 8px;
        }
        .floating.absolute .panel {
          width: 100%;
        }
        .floating.absolute .form {
          gap: 8px;
          padding: 12px;
        }
        .floating.absolute .send {
          padding: 0 16px;
        }
        .floating.fixed { right: 16px; left: auto; bottom: 16px; }
        .floating.fixed.open {
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          padding: 0 16px;
        }
        .floating.fixed.open .panel { width: min(420px, 100%); }

        :host([mobile-fullscreen="true"]) .floating.fixed { left: 0; right: 0; bottom: 0; }

        /* Only go fullscreen if explicitly enabled */
        :host([mobile-fullscreen="true"]) .floating.fixed .panel {
          width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }
        .teaser {
          bottom: 64px;
          right: 0;
        }
      }
      @media (max-width: 480px) {
        .suggest-list {
          flex-direction: column;
          align-items: stretch;
        }
        .suggest-chip {
          width: 100%;
          max-width: 100%;
          margin-bottom: 6px;
          text-align: left;
        }
      }
      .panel.guided { width: 100%; height: auto; max-height: none; padding: 0 24px; border: 0; border-radius: 0; box-shadow: none; overflow: visible; color: var(--guided-text); }
      .guided > .header { margin-inline: -24px; }
      .guided .header { letter-spacing: 0; }
      .guided .header-title { white-space: normal; overflow-wrap: anywhere; }
      .guided .body { display: block; flex: none; }
      .guided-results { padding: 20px 0; line-height: 1.6; font-size: 15px; overflow-wrap: anywhere; }
      .guided-answer-content > :first-child { margin-top: 0; }
      .guided-answer-content > :last-child { margin-bottom: 0; }
      .guided .product-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr)); align-items: start; }
      .guided .product-card { width: 100%; max-width: none; grid-template-columns: minmax(0, 1fr); border-radius: 6px; box-shadow: none; }
      .guided .product-image-wrap { height: 144px; border-radius: 4px; }
      .guided .product-ref { font-size: 12px; line-height: 1.4; color: #4b5563; }
      .guided .product-cta { min-height: 44px; border-radius: 4px; font-size: 12px; white-space: normal; }
      .guided-answer { padding: 14px; border: 1px solid var(--prompt-border); border-radius: 4px; }
      .guided-answer-label { margin: 0 0 8px; font-size: 14px; }
      .guided-summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; margin: 0 0 16px; font-size: 14px; }
      .guided-summary dt { font-weight: 600; }
      .guided-summary dd { margin: 2px 0 0; white-space: pre-wrap; }
      .guided-form { padding: 12px 0; font-size: 14px; }
      .guided-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
      .guided-form label { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .guided-form input, .guided-input { width: 100%; min-width: 0; padding: 12px; border: 1px solid var(--prompt-border); border-radius: 4px; background: var(--panel-bg, #fff); color: inherit; font: inherit; }
      .guided-help { font-size: 13px; line-height: 1.4; }
      .guided-controls { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
      .guided-controls button { min-height: 44px; padding: 6px 12px; border: 1px solid var(--action-border); border-radius: 4px; cursor: pointer; font-size: 13px; }
      .guided-submit { background: var(--action-bg); color: var(--action-text); }
      .guided-edit { background: transparent; color: inherit; }
      .guided-open-question { background: transparent; color: inherit; }
      .guided-controls button:disabled { opacity: .55; cursor: default; }
      .guided :is(button, input, textarea):focus-visible { outline: 2px solid var(--action-bg); outline-offset: 3px; }
      .guided .thinking-wrap { display: none; }
      @media (max-width: 640px) {
        .panel.guided { padding-inline: 16px; }
        .guided > .header { margin-inline: -16px; }
        .guided-fields, .guided-summary { grid-template-columns: minmax(0, 1fr); }
      }
    `;
  }

  private render() {
    this.ensureRoot();
    const guided = this.isGuided();
    const previousInput = this.root.querySelector(".input, .guided-input") as HTMLInputElement | HTMLTextAreaElement | null;
    const previousActive = this.root.activeElement as HTMLElement | null;
    const preserveComposer = {
      focused: previousInput != null && previousActive === previousInput,
      value: previousInput?.value ?? (guided ? this.guidedDraft : ""),
      start: previousInput?.selectionStart ?? null,
      end: previousInput?.selectionEnd ?? null,
    };
    const previousMessages = this.root.querySelector(".messages") as HTMLDivElement | null;
    this.preRenderMessagesScrollTop = previousMessages?.scrollTop ?? null;
    // Safety net: if a rerender happens without an explicit scroll intent, keep
    // the current viewport position instead of letting the rebuilt DOM snap to top.
    if (previousMessages && !this.pendingScroll) {
      this.pendingScroll = "preserve";
    }
    const variant = this.getVariant();
    const hasEmbedKey = !!this.getEmbedKey();
    const hasSessionTokenOverride = !!this.getSessionTokenOverride();

    const needsRemoteTheme =
      hasEmbedKey &&
      !this.getThemeOverride() &&
      !this.cachedTheme &&
      !this.state.session?.theme;
    if ((needsRemoteTheme || (hasEmbedKey && !hasSessionTokenOverride)) && !this.sessionHydrated) {
      // prevent “stock → client” flash on first paint
      // keep space for inline/hero to avoid layout jump
      const wrapperClass =
        variant === "floating"
          ? `wrap floating ${this.getFloatingMode()}`
          : variant === "hero"
            ? "wrap hero"
            : "wrap inline";

      const placeholderPanel =
        variant === "hero" ? "panel hero" : variant === "inline" ? "panel inline" : "panel";

      this.root.innerHTML = `
        <style>${this.css()}</style>
        <div class="${wrapperClass}">
          <div class="${placeholderPanel}" style="visibility:hidden"></div>
        </div>
      `;
      return;
    }


    const session = this.state.session;
    if (session?.show_widget === false) {
      this.state.open = false;
      this.state.showTeaser = false;
      const wrapperClass =
        variant === "floating"
          ? `wrap floating ${this.getFloatingMode()}`
          : variant === "hero"
            ? "wrap hero"
            : "wrap inline";
      const placeholderPanel =
        variant === "hero" ? "panel hero" : variant === "inline" ? "panel inline" : "panel";
      this.root.innerHTML = `
        <style>${this.css()}</style>
        <div class="${wrapperClass}">
          <div class="${placeholderPanel}" style="visibility:hidden"></div>
        </div>
      `;
      return;
    }
    const localeOverride = this.getLocaleOverride();
    let locale = localeOverride || this.state.locale;
    if (!localeOverride && session?.locale_auto) {
      const detected = this.detectWebsiteLocale();
      const enabledLocales = normalizeEnabledLocales(session?.enabled_locales, session?.locale || locale);
      if (detected && enabledLocales.includes(detected) && detected !== locale) {
        locale = detected;
        this.state.locale = detected;
      }
    }
    const t = UI[locale];
    const sessionReady = !!session;
    if (sessionReady && !this.state.promptMeta.teaser) {
      this.state.promptMeta.teaser = t.teaser;
    }
    const welcomeEnabledOverride = this.getWelcomeMessageEnabledOverride();
    const welcomeMessagesOverride = this.getWelcomeMessagesOverride();
    const welcomeFallbackOverride = this.getWelcomeFallbackOverride();
    const welcomeMessageEnabled = welcomeEnabledOverride ?? session?.welcome_message_enabled === true;
    const welcomeMessages = welcomeMessagesOverride ?? session?.welcome_messages;
    const defaultLocale = session?.locale || locale;
    const configuredWelcome =
      welcomeMessageEnabled
        ? welcomeMessages?.[locale] ||
          welcomeMessages?.[defaultLocale] ||
          welcomeMessages?.en ||
          welcomeMessages?.es ||
          ""
        : "";
    const welcomeFallback = (welcomeFallbackOverride ?? session?.welcome_fallback ?? "").trim();
    // Opening-message resolution order:
    // 1. configured welcome
    // 2. smart runtime-generated fallback
    // 3. built-in stock greeting as emergency fallback
    const welcomeText = sessionReady ? (configuredWelcome || welcomeFallback || t.welcome) : "";
    const showSuggestedPrompts = sessionReady && session?.prompt_visibility?.showSuggestedPrompts === true;
    const showContextualTeaser = sessionReady && session?.prompt_visibility?.showContextualTeaser === true;
    const theme = this.getThemeOverride() || session?.theme || this.cachedTheme || {};

    const panelHex = (theme.panelBg || "").trim();
    const panelOpacity =
      typeof theme.panelOpacity === "number" ? clamp01(theme.panelOpacity) : 1;

    const panelRgba = panelHex ? rgbaFromHex(panelHex, panelOpacity) : null;


    const brand = (this.getBrandOverride() || session?.brand || this.cachedBrand || "").trim();
    const launcherLabelOverride = resolveWidgetPresentationText(theme, locale, "launcherLabel") || (session?.locale === locale ? session.launcher_label : "") || "";
    const headerTitleOverride = resolveWidgetPresentationText(theme, locale, "headerTitle") || (session?.locale === locale ? session.header_title : "") || "";
    const pillText = launcherLabelOverride || t.pill(brand);
    const pillByline = resolveLauncherByline({ ...session?.theme, ...theme }, locale);
    const headerTitleText = headerTitleOverride || t.title(brand);

    const header = splitPair(theme.headerBg, { bg: "#111827", text: "#ffffff" });
    const user = splitPair(theme.bubbleUser, { bg: "#2563eb", text: "#ffffff" });
    const bot = splitPair(theme.bubbleBot, { bg: "#f3f4f6", text: "#111827" });
    const send = splitPair(theme.sendBg, { bg: "#2563eb", text: "#ffffff" });
    const promptBg = `color-mix(in srgb, ${send.bg} 8%, #ffffff)`;
    const promptBorder = rgbaFromHex(send.bg, 0.25) || "rgba(37,99,235,0.25)";
    const promptHover = `color-mix(in srgb, ${send.bg} 15%, #ffffff)`;
    const promptText = contrastTextFromHex("#ffffff");
    const hideHeader = this.getHideHeader();
    const showBranding =
      this.getShowBrandingOverride() ?? Boolean(session?.show_branding);
    const showMedicalDisclaimer = Boolean(session?.medical_disclaimer_enabled);
    const medicalDisclaimerText = (
      session?.medical_disclaimer_text ||
      (locale === "es"
        ? "Información general. No es consejo médico, diagnóstico, tratamiento ni atención de emergencia. En emergencias, llama al 112 o a los servicios de emergencia locales."
        : "General information only. Not medical advice, diagnosis, treatment, or emergency care. For emergencies, call 911 or local emergency services.")
    ).trim();
    const rawMedicalDisclaimerUrl = (session?.medical_disclaimer_url || `/${locale}/legal/medical-disclaimer`).trim();
    const medicalDisclaimerHref = rawMedicalDisclaimerUrl.startsWith("http://") || rawMedicalDisclaimerUrl.startsWith("https://")
      ? rawMedicalDisclaimerUrl
      : `${this.getApiBase()}${rawMedicalDisclaimerUrl.startsWith("/") ? rawMedicalDisclaimerUrl : `/${rawMedicalDisclaimerUrl}`}`;
    const medicalDisclaimerLinkText = locale === "es" ? "Más información" : "Learn more";
    const showWidgetFooter = showBranding || showMedicalDisclaimer;
    const showHeaderIcon = Boolean(session?.show_header_icon);
    const headerLogoUrl = (theme.headerLogoUrl || "").trim();
    const shouldShowHeaderLogo = showHeaderIcon && headerLogoUrl.length > 0;
    const safeHeaderLogoUrl = shouldShowHeaderLogo ? escapeAttr(headerLogoUrl) : "";

    const open = variant !== "floating" ? true : this.state.open;
    const hasSessionToken = !!session?.token || !!this.getSessionTokenOverride();
    const showTokenTroubleshooting = open && this.sessionHydrated && !hasSessionToken;
    const sessionTroubleshootingBody = this.getSessionRefreshUrlOverride()
      ? t.errors.tokenTroubleshootSupport
      : t.errors.tokenTroubleshootEmbed;

    const openNow = open;
    if (this.shouldLockPageScroll()) this.lockPageScroll();
    else this.unlockPageScroll();

    const animateIn = openNow && !this.lastRenderOpen;
    this.lastRenderOpen = openNow;

    const floatingMode = this.getFloatingMode();
    const wrapperClass =
      variant === "floating"
        ? `wrap floating ${floatingMode}${openNow ? " open" : ""}`
        : variant === "hero"
          ? "wrap hero"
          : "wrap inline";

    const basePanelClass =
      variant === "hero" ? "panel hero" : variant === "inline" ? "panel inline" : "panel";

    const panelClass = animateIn ? `${basePanelClass} animate-in` : basePanelClass;
    const panelClassWithHeader = `${hideHeader ? `${panelClass} no-header` : panelClass}${guided ? " guided" : ""}`;
    const composerSafePx = showWidgetFooter ? (showMedicalDisclaimer ? 156 : 128) : 88;
    const panelCssVars = [
      panelRgba ? `--panel-bg:${panelRgba}` : "",
      `--composer-safe:${composerSafePx}px`,
      `--prompt-bg:${promptBg}`,
      `--prompt-border:${promptBorder}`,
      `--prompt-hover:${promptHover}`,
      `--prompt-text:${promptText}`,
      `--action-bg:${send.bg}`,
      `--action-text:${send.text}`,
      `--action-border:${rgbaFromHex(send.bg, 0.55) || send.bg}`,
      `--action-border-hover:${rgbaFromHex(send.bg, 0.8) || send.bg}`,
      `--action-shadow:${rgbaFromHex(send.bg, 0.22) || "rgba(0,0,0,0.10)"}`,
      ...(guided ? [`--guided-text:${contrastTextFromHex(panelHex || "#ffffff")}`] : []),
    ]
      .filter(Boolean)
      .join(";");
    const panelStyleAttr = panelCssVars ? `style="${panelCssVars}"` : "";

    const msgs = this.state.msgs;
    const opening = extractOpeningQuestionSettings({ widget: { prompts: session?.prompt_visibility } });
    const requestedOpeningLocale = this.getAttribute("locale")?.trim().toLowerCase() ?? locale;
    // Session lists are already entitlement/locale filtered. Page groups are
    // configured presentation, not a new factual authority or model call.
    const suggestedEntries = resolveOpeningQuestions(opening, requestedOpeningLocale, this.entryContext?.url ?? window.location.href);
    const suggestedPrompts = suggestedEntries.map((item) => item.question);
    const suggestedHtml =
      showSuggestedPrompts && (!this.state.hasUserSentMessage || this.entryQuestionsVisible) && suggestedPrompts.length > 0
        ? `<div class="suggest-wrap">
            <div class="suggest-label">${escapeHtml(t.suggestedLabel)}</div>
            <div class="suggest-list">
                ${suggestedPrompts
                  .map(
                    (prompt, idx) =>
                    `<button class="suggest-chip" type="button" role="button" tabindex="0" aria-label="${escapeHtml(prompt)}" ${this.state.busy ? "disabled" : ""} data-prompt-index="${idx}"><span class="entry-label">${escapeHtml(suggestedEntries[idx].label || prompt)}</span><span class="entry-chevron" aria-hidden="true"></span></button>`,
                  )
                  .join("")}
            </div>
          </div>`
        : "";
    const quickReplyHtml =
      showSuggestedPrompts && this.state.quickReplies.length > 0 && this.state.hasUserSentMessage && !this.state.busy
        ? `<div class="row bot" id="quick-replies">
            <div class="bubble bot" style="--bg:${bot.bg};--fg:${bot.text};background:var(--bg);color:var(--fg);">
              <div class="suggest-label">${escapeHtml(t.quickReplyLabel)}</div>
              <div class="suggest-list">
                ${this.state.quickReplies
                  .slice(0, 4)
                  .map(
                    (prompt, idx) =>
                      `<button class="suggest-chip" type="button" role="button" tabindex="0" aria-label="Suggested question" data-quick-index="${idx}">${escapeHtml(prompt)}</button>`,
                  )
                  .join("")}
              </div>
            </div>
          </div>`
        : "";
    const shouldRenderInitialAssistantMessage =
      sessionReady && this.sessionHydrated && msgs.length === 0 && welcomeText.length > 0;
    const messagesHtml =
      msgs.length === 0
        ? shouldRenderInitialAssistantMessage
          ? `<div class="${guided ? "guided-introduction" : "row bot"}" id="msg-0">
              <div class="${guided ? "guided-answer-content" : "bubble bot anim"}" ${guided ? "" : `style="--bg:${bot.bg};--fg:${bot.text};background:var(--bg);color:var(--fg);"`}>
                ${escapeHtml(welcomeText)}
              </div>
            </div>${suggestedHtml}`
          : ""
        : msgs
            .map((m, i) => {
              const isUser = m.role === "user";
              if (guided && (isUser || msgs.slice(i + 1).some(message => message.role === "assistant"))) return "";
              const isLast = i === msgs.length - 1;
              const bubbleStyle = isUser
                ? `--bg:${user.bg};--fg:${user.text};background:var(--bg);color:var(--fg);`
                : `--bg:${bot.bg};--fg:${bot.text};background:var(--bg);color:var(--fg);`;

              const { body, source } = splitBodyAndSource(m.content, isUser);
              const sourceMarkerPattern = /\[PRODUCT:([a-z0-9][a-z0-9-]{0,120})\]/gi;
              const sourceMarkers = !isUser ? source?.match(sourceMarkerPattern) ?? [] : [];
              const visibleSource = !isUser ? source?.replace(sourceMarkerPattern, " ").trim() : source;
              const inlineProductAction = !isUser && Array.isArray(m.actions)
                ? m.actions.find((a): a is Extract<Action, { type: "product_cards" }> =>
                    a.type === "product_cards" && a.presentation === "inline_markers"
                  ) ?? null
                : null;
              const visibleActions = !isUser && Array.isArray(m.actions)
                ? m.actions.filter((a) => a !== inlineProductAction)
                : [];
              const bodyHtml = inlineProductAction
                ? formatMessageHtmlWithInlineProductCards([body, ...sourceMarkers].join("\n"), inlineProductAction.products, this.state.locale, Boolean(this.productActionHandler), this.productResults, guided)
                : formatMessageHtml(body);
              return `<${guided ? "section" : "div"} class="${guided ? "guided-answer" : `row ${isUser ? "user" : "bot"}`}" id="msg-${i}">
                ${guided ? `<h3 class="guided-answer-label">${locale === "es" ? "Asesor:" : "Advisor:"}</h3>` : ""}<div class="${guided ? "guided-answer-content" : `bubble ${isUser ? "user" : "bot"} ${!isUser && m.actions?.some(action => action.type === "product_cards") ? "with-products" : ""} ${isLast ? "anim" : ""}`}" ${guided ? "" : `style="${bubbleStyle}"`}>
                  ${bodyHtml}
                  ${
                    visibleActions.length
                      ? `<div class="actions">
                          ${visibleActions
                            .map((a) => {
                              if (a.type === "link") {
                                return `<a class="action" href="${escapeHtml(a.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(a.label)}</a>`;
                              }
                              if (a.type === "call") {
                                const href = `tel:${String(a.phone).replace(/[^+\d]/g, "")}`;
                                return `<a class="action" href="${escapeHtml(href)}">${escapeHtml(a.label)}</a>`;
                              }
                              if (a.type === "handoff") {
                                return `<button class="action-btn" type="button" data-action="handoff" data-i="${i}">${escapeHtml(a.label)}</button>`;
                              }
                              if (a.type === "lead_form") {
                                const fields = Array.isArray(a.fields) ? a.fields : ["name", "email"];
                                const rows = fields
                                  .map((f) => {
                                    const label =
                                      f === "name" ? t.lead.name : f === "phone" ? t.lead.phone : t.lead.email;
                                    const type = f === "email" ? "email" : f === "phone" ? "tel" : "text";
                                    return `<label class="lead-field">
                                      <span class="lead-label">${escapeHtml(label)}</span>
                                      <input class="lead-input" name="${f}" type="${type}" required />
                                    </label>`;
                                  })
                                  .join("");
                                const consent = `<label class="lead-consent">
                                  <input type="checkbox" name="consent" required />
                                  <span>${escapeHtml(t.lead.consent)}</span>
                                </label>
                                <div class="lead-consent-note">${escapeHtml(t.lead.consentNote)}</div>`;
                                return `<form class="lead-form" data-action="lead-form" data-i="${i}">
                                  ${rows}
                                  ${consent}
                                  <button class="lead-submit" type="submit">${escapeHtml(t.lead.submit)}</button>
                                </form>`;
                              }
                              if (a.type === "product_cards") {
                                const products = Array.isArray(a.products) ? a.products : [];
                                if (!products.length) return "";
                                return `<div class="product-cards">
                                  ${products
                                    .map((product) => renderProductCardHtml(product, this.state.locale, Boolean(this.productActionHandler), product.commerce ? this.productResults.get(product.commerce.productId) : undefined, guided))
                                    .join("")}
                                </div>`;
                              }
                              return "";
                            })
                            .join("")}
                        </div>`
                      : ""
                  }
                  ${
                    !isUser && visibleSource
                      ? `<div class="source-foot">${formatMessageHtml(visibleSource)}</div>`
                      : ""
                  }
                </div>
              </${guided ? "section" : "div"}>`;
            })
            .join("") + (guided ? "" : quickReplyHtml);

    const briefLabels = locale === "es"
      ? { use: "Uso o proyecto", setting: "Entorno", appearance: "Aspecto o estilo", requirements: "Requisitos" }
      : { use: "Use or project", setting: "Setting", appearance: "Appearance or style", requirements: "Requirements" };
    const guidedBriefVisible = !this.state.hasUserSentMessage || this.guidedEditing;
    const help = guidedFieldHelp(this.getAttribute("guided-field-help"), locale);
    const latestMessage = this.state.msgs[this.state.msgs.length - 1];
    const showGuidedInput = !guidedBriefVisible && !this.state.busy &&
      (this.guidedFollowUpOpen || latestMessage?.role === "assistant");
    const guidedSummary = this.guidedSubmitted ? `<dl class="guided-summary" aria-label="${locale === "es" ? "Detalles enviados" : "Submitted brief"}">${(Object.keys(briefLabels) as Array<keyof typeof briefLabels>).filter(key => this.guidedSubmitted![key].trim()).map(key => `<div><dt>${briefLabels[key]}</dt><dd>${escapeHtml(this.guidedSubmitted![key])}</dd></div>`).join("")}</dl>` : "";
    const guidedForm = `<form class="guided-form" aria-label="${locale === "es" ? "Detalles del proyecto" : "Project brief"}">
      ${guidedBriefVisible ? `<div class="guided-fields">${(Object.keys(briefLabels) as Array<keyof typeof briefLabels>).map(key => `<label><span>${briefLabels[key]}</span>${help[key]?.help ? `<span class="guided-help">${escapeHtml(help[key]!.help)}</span>` : ""}<input data-brief-field="${key}" maxlength="1500" placeholder="${escapeAttr(help[key]?.example ?? "")}" value="${escapeAttr(this.guidedBrief[key])}" ${this.state.busy || !hasSessionToken ? "disabled" : ""} /></label>`).join("")}</div>`
        : showGuidedInput ? `<input class="guided-input" aria-label="${locale === "es" ? "Consulta" : "Question"}" maxlength="6000" ${!hasSessionToken ? "disabled" : ""} />` : ""}
      ${this.state.busy ? `<div role="status">${locale === "es" ? "Consultando…" : "Checking…"}</div>` : ""}
      <div class="guided-controls">${guidedBriefVisible || showGuidedInput ? `<button class="guided-submit" type="submit" ${this.state.busy || !hasSessionToken || (guidedBriefVisible ? !Object.values(this.guidedBrief).some(value => value.trim()) : !preserveComposer.value.trim()) ? "disabled" : ""}>${guidedBriefVisible ? (locale === "es" ? "Buscar opciones" : "Find options") : (locale === "es" ? "Consultar" : "Ask")}</button>` : ""}
      ${!guidedBriefVisible && !showGuidedInput && !this.state.busy ? `<button class="guided-open-question" type="button">${locale === "es" ? "Otra consulta" : "Ask a question"}</button>` : ""}
      ${this.state.hasUserSentMessage ? `<button class="guided-edit" type="button" ${this.state.busy ? "disabled" : ""}>${guidedBriefVisible ? (locale === "es" ? "Volver a los resultados" : "Back to results") : (locale === "es" ? "Modificar detalles" : "Edit brief")}</button>` : ""}</div></form>`;
            

    // floating closed => pill only
    if (variant === "floating" && !open) {
      this.lastRenderOpen = false;
      if (this.getAttribute("launcher-visibility") === "hidden") {
        this.root.innerHTML = `<style>${this.css()}</style>`;
        return;
      }
      const hasToken = !!session?.token || !!this.getSessionTokenOverride();
      if (!hasToken) {
        this.root.innerHTML = `<style>${this.css()}</style>`;
        return;
      }

      this.root.innerHTML = `
        <style>${this.css()}${widgetEntryStyles}</style>
        <div class="${wrapperClass}">
          <div class="launcher-wrap">
            ${
              showContextualTeaser && this.state.showTeaser
                ? `<aside class="entry-dock" style="--prompt-border:${promptBorder};--prompt-bg:${promptBg};--prompt-text:${promptText};"><div class="entry-dock-head"><span>${escapeHtml(headerTitleText)}</span><button class="entry-dismiss" aria-label="${locale === "es" ? "Cerrar invitación" : "Dismiss invitation"}">×</button></div><h3>${locale === "es" ? "¿Cómo podemos ayudarte?" : "How can we help?"}</h3>${showSuggestedPrompts && suggestedEntries.length ? suggestedEntries.slice(0, 2).map((item, idx) => `<button class="suggest-chip" data-dock-index="${idx}" aria-label="${escapeHtml(item.question)}"><span class="entry-label">${escapeHtml(item.label || item.question)}</span><span aria-hidden="true">›</span></button>`).join("") : `<button class="suggest-chip entry-open" type="button"><span class="entry-label">${escapeHtml(pillText)}</span><span aria-hidden="true">›</span></button>`}</aside>`
                : ""
            }
            <button class="pill" style="background:${send.bg};color:${send.text};"><span class="entry-icon" aria-hidden="true">${widgetDialogueIcon}</span><span class="entry-copy"><strong>${escapeHtml(pillText)}</strong>${pillByline ? `<small>${escapeHtml(pillByline)}</small>` : ""}</span><span class="entry-chevron" aria-hidden="true">⌃</span></button>
          </div>
        </div>
      `;
      this.root
        .querySelectorAll(".action-btn[data-action='handoff']")
        .forEach((btn) => {
          btn.addEventListener("click", () => {
            // Temporary: send a message that triggers your server’s lead-intent flow
            void this.send("I’d like a human follow-up.");
          });
        });

      const btn = this.root.querySelector(".pill") as HTMLButtonElement | null;
      this.root.querySelector(".entry-dismiss")?.addEventListener("click", () => { this.markTeaserSeen(); this.render(); });
      this.root.querySelectorAll("[data-dock-index]").forEach((el) => el.addEventListener("click", () => {
        const question = suggestedEntries[Number((el as HTMLElement).dataset.dockIndex)];
        if (question) void this.askQuestion({ questionId: question.id });
      }));
      const teaser = this.root.querySelector(".teaser") as HTMLDivElement | null;
      teaser?.addEventListener("click", () => {
        this.markTeaserSeen();
        this.state.open = true;
        this.pendingFocus = true;
        const lastRole = this.state.msgs[this.state.msgs.length - 1]?.role;
        this.pendingScroll = lastRole === "assistant" ? "lastAssistantStart" : "lastUserStart";
        this.render();
      });
      btn?.addEventListener("click", () => {
        this.markTeaserSeen();
        this.state.open = true;
        this.pendingFocus = true;
        this.savePersisted(false);
        const lastRole = this.state.msgs[this.state.msgs.length - 1]?.role;
        this.pendingScroll = lastRole === "assistant" ? "lastAssistantStart" : "lastUserStart";
        this.render();
      });
      this.root.querySelector(".entry-open")?.addEventListener("click", () => btn?.click());

      return;
    }

    const brandingLogoUrl = `${this.getApiBase()}/widget/v1/aliigo.svg`;
    this.root.innerHTML = `
      <style>${this.css()}${widgetEntryStyles}</style>
      <div class="${wrapperClass}">
      <div class="${panelClassWithHeader}" ${panelStyleAttr}>
          ${
            hideHeader
              ? ""
              : `<div class="header ${shouldShowHeaderLogo ? "has-logo" : ""}" style="background:${header.bg};color:${header.text};">
            <div class="header-main">
              ${shouldShowHeaderLogo ? `<img class="header-icon" src="${safeHeaderLogoUrl}" alt="" onerror="this.style.display='none'" />` : ``}
              <span class="header-title">${escapeHtml(headerTitleText)}</span>
            </div>
            ${variant === "floating" ? `<button class="close" aria-label="Close" style="color:${header.text};">×</button>` : ``}
          </div>`
          }

          <div class="body">
            <div class="${guided ? "guided-results" : "messages"}" ${guided ? 'aria-live="polite" aria-busy="' + String(this.state.busy) + '"' : ""}>${guided ? guidedSummary : ""}${messagesHtml}${msgs.length > 0 && this.entryQuestionsVisible ? suggestedHtml : ""}<div id="bottom"></div></div>
            <div class="thinking-wrap ${this.state.busy && this.state.isThinking ? "visible" : ""}">
              <div class="thinking-hint" aria-hidden="true"></div>
              <div class="typing" style="background:${bot.bg};">
                <span class="typing-dot" style="background:${bot.text};"></span>
                <span class="typing-dot" style="background:${bot.text};"></span>
                <span class="typing-dot" style="background:${bot.text};"></span>
              </div>
            </div>
          </div>

          ${
            showTokenTroubleshooting
              ? `<div class="token-help" role="status" aria-live="polite">
            <div class="token-help-title">${escapeHtml(t.errors.tokenUnavailable)}</div>
            <div class="token-help-body">${escapeHtml(sessionTroubleshootingBody)}</div>
          </div>`
              : ""
          }

          ${guided ? guidedForm : `<form class="form">
            <input class="input" placeholder="${showTokenTroubleshooting ? t.errors.tokenUnavailable : t.placeholder}" ${this.state.busy || !hasSessionToken ? "disabled" : ""} />
            <button class="send" type="submit" style="background:${send.bg};color:${send.text};" ${this.state.busy || !hasSessionToken ? "disabled" : ""}>${t.send}</button>
          </form>`}
          ${
            showWidgetFooter
              ? `<div class="powered-wrap">
            ${
              showMedicalDisclaimer
                ? `<div class="medical-disclaimer${showBranding ? " with-branding" : ""}">${escapeHtml(medicalDisclaimerText)} <a href="${escapeAttr(medicalDisclaimerHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(medicalDisclaimerLinkText)}</a></div>`
                : ""
            }
            ${
              showBranding
                ? `<div class="powered-brand-row"><a class="powered-link" href="https://aliigo.com" target="_blank" rel="noopener noreferrer">
              <span class="powered-prefix">Powered by</span>
              <img class="powered-logo" src="${brandingLogoUrl}" alt="Aliigo logo" />
            </a></div>`
                : ""
            }
          </div>`
              : ""
          }
        </div>
      </div>
    `;

    const close = this.root.querySelector(".close") as HTMLButtonElement | null;
    const closeWidget = (event?: Event) => {
      event?.preventDefault();
      event?.stopPropagation();
      this.state.open = false;
      this.pendingFocus = false;
      this.savePersisted(false);
      this.render();
      if (this.getVariant() === "floating") {
        const target = this.returnFocusTarget;
        this.returnFocusTarget = null;
        if (target?.isConnected) target.focus({ preventScroll: true });
        if (!target?.isConnected || document.activeElement !== target) {
          this.root.querySelector<HTMLButtonElement>(".pill")?.focus({ preventScroll: true });
        }
      }
    };
    close?.addEventListener("pointerdown", closeWidget);
    close?.addEventListener("touchend", closeWidget);
    close?.addEventListener("click", closeWidget);
    this.root.querySelector<HTMLElement>(".panel")?.addEventListener("keydown", event => {
      if (event.key === "Escape" && !event.defaultPrevented && !event.isComposing && this.getVariant() === "floating") closeWidget(event);
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-cart-product]").forEach(button => button.addEventListener("click", () => {
      const id = button.dataset.cartProduct;
      const products = this.state.msgs.flatMap(message => message.actions?.flatMap(action => action.type === "product_cards" ? action.products : []) ?? []);
      const product = products.find(item => item.commerce?.productId === id &&
        productQuoteKey(item) === button.dataset.cartQuote);
      if (product) void this.addProduct(product);
    }));
    this.root.querySelectorAll<HTMLButtonElement>("[data-select-product]").forEach(button => button.addEventListener("click", () => {
      const index = /^msg-(\d+)$/.exec(button.closest(".guided-answer, .row")?.id ?? "")?.[1];
      const message = index === undefined ? undefined : this.state.msgs[Number(index)];
      const products = message?.role === "assistant"
        ? message.actions?.flatMap(action => action.type === "product_cards" ? action.products : []) ?? [] : [];
      const product = products.find(item => item.discovery?.productId === button.dataset.selectProduct && item.discovery?.catalogId === button.dataset.selectCatalog);
      if (product) dispatchWidgetProductSelection(this, product, createRequestId());
    }));

    this.root.querySelectorAll<HTMLInputElement>("[data-brief-field]").forEach(input => input.addEventListener("input", () => {
      const key = input.dataset.briefField as keyof typeof briefLabels;
      if (Object.prototype.hasOwnProperty.call(this.guidedBrief, key)) this.guidedBrief[key] = input.value;
      const submit = this.root.querySelector<HTMLButtonElement>(".guided-submit");
      if (submit) submit.disabled = this.state.busy || !hasSessionToken || !Object.values(this.guidedBrief).some(value => value.trim());
    }));
    this.root.querySelector(".guided-edit")?.addEventListener("click", () => {
      this.guidedEditing = !this.guidedEditing;
      this.render();
      this.root.querySelector<HTMLInputElement | HTMLTextAreaElement>("[data-brief-field], .guided-input")?.focus({ preventScroll: true });
    });
    this.root.querySelector(".guided-open-question")?.addEventListener("click", () => {
      this.guidedFollowUpOpen = true;
      this.render();
      this.root.querySelector<HTMLInputElement>(".guided-input")?.focus({ preventScroll: true });
    });
    const form = this.root.querySelector(".form, .guided-form") as HTMLFormElement | null;
    const input = this.root.querySelector(".input, .guided-input") as HTMLInputElement | HTMLTextAreaElement | null;
    if (guided) input?.addEventListener("input", () => {
      this.guidedDraft = input.value;
      const submit = this.root.querySelector<HTMLButtonElement>(".guided-submit");
      if (submit) submit.disabled = this.state.busy || !hasSessionToken || !input.value.trim();
    });
    if (input && preserveComposer.value) {
      input.value = preserveComposer.value;
    }
    if (input && preserveComposer.focused) {
      input.focus({ preventScroll: true });
      if (preserveComposer.start !== null && preserveComposer.end !== null) {
        try {
          input.setSelectionRange(preserveComposer.start, preserveComposer.end);
        } catch {}
      }
    }

    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      if (this.state.busy || (guided && !guidedBriefVisible && !showGuidedInput)) return;
      const v = guided && guidedBriefVisible
        ? (Object.keys(briefLabels) as Array<keyof typeof briefLabels>).filter(key => this.guidedBrief[key].trim()).map(key => `${briefLabels[key]}: ${this.guidedBrief[key].trim()}`).join("\n")
        : (input?.value || "").trim();
      if (!v) return;
      if (input && !guided) input.value = "";
      if (window.matchMedia("(max-width: 768px)").matches) {
        input?.blur();
      }
      this.pendingFocus = true;
      void this.send(v, undefined, undefined, guided ? { guidedSubmission: true, guidedBrief: guidedBriefVisible ? { ...this.guidedBrief } : undefined } : undefined);
    });

    this.root.querySelectorAll(".suggest-chip[data-prompt-index]").forEach((el) => {
      const btn = el as HTMLButtonElement;
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.promptIndex || "");
        if (Number.isNaN(idx)) return;
        const prompt = suggestedPrompts[idx] || "";
        if (!prompt) return;
        this.pendingFocus = true;
        this.entryQuestionsVisible = false;
        void this.send(prompt);
      });
      btn.addEventListener("keydown", (evt) => {
        if (evt.key === "Enter") {
          evt.preventDefault();
          btn.click();
        }
      });
    });

    this.root.querySelectorAll(".suggest-chip[data-quick-index]").forEach((el) => {
      const btn = el as HTMLButtonElement;
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.quickIndex || "");
        if (Number.isNaN(idx)) return;
        const prompt = this.state.quickReplies[idx] || "";
        if (!prompt) return;
        this.pendingFocus = true;
        void this.send(prompt);
      });
      btn.addEventListener("keydown", (evt) => {
        if (evt.key === "Enter") {
          evt.preventDefault();
          btn.click();
        }
      });
    });

    this.root.querySelectorAll(".lead-form").forEach((el) => {
      const formEl = el as HTMLFormElement;
      formEl.addEventListener("submit", (e) => {
        e.preventDefault();
        const fd = new FormData(formEl);
        const lead = {
          name: (fd.get("name") || "").toString().trim(),
          email: (fd.get("email") || "").toString().trim(),
          phone: (fd.get("phone") || "").toString().trim(),
          consent: (fd.get("consent") || "") === "on",
        };

        if (!lead.name && !lead.email && !lead.phone) return;
        if (!lead.consent) return;

        if (window.matchMedia("(max-width: 768px)").matches) {
          const active = document.activeElement as HTMLElement | null;
          active?.blur();
        }
        formEl.classList.add("sent");
        const btn = formEl.querySelector(".lead-submit") as HTMLButtonElement | null;
        if (btn) btn.textContent = t.lead.sent;

        const messageIndex = Number(formEl.dataset.i || "");
        if (!Number.isNaN(messageIndex)) {
          const msg = this.state.msgs[messageIndex];
          if (msg?.actions?.length) {
            msg.actions = msg.actions.filter((a) => a.type !== "lead_form");
          }
          // Remove the assistant bubble that asked for details to avoid duplication
          const nextMsgs = this.state.msgs.slice();
          nextMsgs.splice(messageIndex, 1);
          this.state.msgs = nextMsgs;
          this.pendingScroll = "bottom";
          this.render();
        }

        void this.send(t.lead.hiddenMessage, lead, t.lead.followUp, {
          silentUser: true,
          suppressReply: true,
        });
      });
    });

    this.applyPendingScroll();
    this.applyPendingFocus();
  }

  private async send(
    content: string,
    lead?: { name?: string; email?: string; phone?: string; consent?: boolean },
    postReply?: string,
    opts?: { silentUser?: boolean; suppressReply?: boolean; guidedBrief?: GuidedBrief; guidedSubmission?: boolean }
  ) {
    if (this.state.busy) return;
    const generation = this.identityGeneration;
    const t = UI[this.state.locale] ?? UI.en;
    await this.refreshSessionIfStale();
    if (generation !== this.identityGeneration) return;
    let session = this.state.session;
    if (!session?.token && this.getSessionTokenOverride()) {
      session = await this.ensureSession();
    }
    if (generation !== this.identityGeneration) return;
    if (!session?.token || this.state.busy) return;

    this.state.busy = true;
    if (this.isGuided()) {
      if (opts?.guidedBrief) this.guidedSubmitted = { ...opts.guidedBrief };
      this.guidedEditing = false;
      this.guidedFollowUpOpen = false;
      if (opts?.guidedSubmission) {
        this.guidedDraft = "";
        const input = this.root.querySelector<HTMLInputElement>(".guided-input");
        if (input) input.value = "";
      }
    }
    this.beginThinkingWindow();
    this.markTeaserSeen();
    this.state.quickReplies = [];
    if (!opts?.silentUser) {
      this.state.hasUserSentMessage = true;
      this.state.msgs = [...this.state.msgs, { role: "user", content }];
    }
    this.pendingFocus = true;
    this.savePersisted(true);
    this.pendingScroll = "lastUserStart";
    this.render();

    const requestId = createRequestId();
    const h1 = (document.querySelector("h1")?.textContent || "").trim();
    const context = this.entryContext ?? normalizeWidgetContext({ pageType: inferPageType(window.location.pathname), entityFocus: h1.slice(0, 160) }, window.location.href);
    const pageMeta = { current_url: context?.url, page_type: context?.pageType, entity_focus: context?.entityFocus };

    try {
      const apiBase = this.getApiBase();
      const res = await this.withTimeout(
        `${apiBase}/api/conversation`,
        {
        method: "POST",
        headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: session.token,
            conversationId: this.state.conversationId,
            request_id: requestId,
            externalRef: this.state.visitorSessionId,
            message: content,
            locale: this.state.locale,
            channel: "web",
            meta: pageMeta,
            ...(lead ? { lead } : {}),
          }),
        },
        25000,
      );

      const raw = (await res.json().catch(() => ({}))) as {
        conversationId?: string;
        reply?: string;
        actions?: unknown;
        meta?: unknown;
        error?: string;
        locale?: string;
      };
      if (generation !== this.identityGeneration) return;

      if (!res.ok) {
        const errText = (raw.error || "").toLowerCase();
        const shouldRefresh = res.status === 401 || res.status === 403 || errText.includes("session expired");
        const isExpired = errText.includes("session expired");

        // Auto-recover from auth/session errors once. When we refresh, drop the
        // server conversation id and retry silently instead of surfacing an
        // internal session message to the visitor.
        if (shouldRefresh) {
          const fresh = await this.tryRefreshSessionToken();
          if (generation !== this.identityGeneration) return;

          if (fresh) {
            this.state.conversationId = null;
            const retry = await this.withTimeout(
              `${apiBase}/api/conversation`,
              {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                token: fresh,
                conversationId: null,
                request_id: requestId,
                externalRef: this.state.visitorSessionId,
                message: content,
                locale: this.state.locale,
                channel: "web",
                meta: pageMeta,
                ...(lead ? { lead } : {}),
              }),
              },
              25000,
            );

            const raw2 = (await retry.json().catch(() => ({}))) as {
              conversationId?: string;
              reply?: string;
              error?: string;
              locale?: string;
              actions?: unknown;
              meta?: unknown;
            };
            if (generation !== this.identityGeneration) return;

            if (retry.ok) {
              if (raw2.conversationId) this.state.conversationId = raw2.conversationId;
              this.applyPromptMeta(raw2.meta, "response");
              const nextActions2 = mapServerActionsToWidget(raw2.actions);
              const cleanedActions2 = lead
                ? nextActions2?.filter((a) => a.type !== "lead_form")
                : nextActions2;

              if (!opts?.suppressReply) {
                this.state.msgs = [
                  ...this.state.msgs,
                  { role: "assistant", content: raw2.reply || "", actions: cleanedActions2, guidedInputRequested: requestsGuidedInput(raw2.meta) },
                ];
              }
              if (postReply) {
                this.state.msgs = [
                  ...this.state.msgs,
                  { role: "assistant", content: postReply },
                ];
              }
              this.pendingFocus = true;
              this.state.busy = false;
              this.endThinkingWindow();
              this.savePersisted(true);
              this.pendingScroll = "lastAssistantStart";
              this.render();
              return;
            }

            // retry failed
            this.reportTelemetry("conversation_retry_failed", {
              status: retry.status,
              error: raw2.error || "retry_failed",
            });
            this.state.msgs = [
              ...this.state.msgs,
              {
                role: "assistant",
                content: normalizeConversationErrorMessage({
                  locale: this.state.locale,
                  status: retry.status,
                  rawError: raw2.error,
                  genericMessage: t.errors.generic,
                }),
              },
            ];
            this.pendingFocus = true;
            this.state.busy = false;
            this.endThinkingWindow();
            this.savePersisted(true);
            this.pendingScroll = "bottom";
            this.render();
            return;
          }
        }

        // default error path
        const friendly = isExpired
          ? t.errors.generic
          : normalizeConversationErrorMessage({
              locale: this.state.locale,
              status: res.status,
              rawError: raw.error,
              genericMessage: t.errors.generic,
            });
        this.reportTelemetry("conversation_failed", {
          status: res.status,
          error: raw.error || "conversation_failed",
        });
        this.state.msgs = [...this.state.msgs, { role: "assistant", content: friendly }];
        this.pendingFocus = true;
        this.state.busy = false;
        this.endThinkingWindow();
        this.savePersisted(true);
        this.pendingScroll = "bottom";
        this.render();
        return;
      }

      if (raw.conversationId) this.state.conversationId = raw.conversationId;
      this.applyPromptMeta(raw.meta, "response");
      const nextActions = mapServerActionsToWidget(raw.actions);
      const cleanedActions = lead
        ? nextActions?.filter((a) => a.type !== "lead_form")
        : nextActions;
      if (!opts?.suppressReply) {
        this.state.msgs = [
          ...this.state.msgs,
          { role: "assistant", content: raw.reply || "", actions: cleanedActions, guidedInputRequested: requestsGuidedInput(raw.meta) },
        ];
      }
      if (postReply) {
        this.state.msgs = [
          ...this.state.msgs,
          { role: "assistant", content: postReply },
        ];
      }
      this.pendingFocus = true;
      this.state.busy = false;
      this.endThinkingWindow();
      this.savePersisted(true);
      this.pendingScroll = "lastAssistantStart";
      this.render();
    } catch (err) {
      if (generation !== this.identityGeneration) return;
      const errMsg = this.errString(err);
      const errName = err instanceof Error ? err.name : "";
      this.reportTelemetry("conversation_exception", { message: errMsg, name: errName });

      const isTransientNetworkError =
        errName === "AbortError" ||
        /network|fetch|failed to fetch|load failed|timeout/i.test(errMsg);

      if (isTransientNetworkError) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 250));
          if (generation !== this.identityGeneration) return;
          const fresh = await this.tryRefreshSessionToken();
          if (generation !== this.identityGeneration) return;
          if (fresh) {
            const apiBase = this.getApiBase();
            const retry = await this.withTimeout(
              `${apiBase}/api/conversation`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  token: fresh,
                  conversationId: this.state.conversationId,
                  request_id: requestId,
                  externalRef: this.state.visitorSessionId,
                  message: content,
                  locale: this.state.locale,
                  channel: "web",
                  meta: {
                    current_url: window.location.href,
                    page_type: inferPageType(window.location.pathname),
                    entity_focus: h1 || undefined,
                  },
                  ...(lead ? { lead } : {}),
                }),
              },
              25000
            );

            const rawRetry = (await retry.json().catch(() => ({}))) as {
              conversationId?: string;
              reply?: string;
              actions?: unknown;
              error?: string;
              meta?: unknown;
            };
            if (generation !== this.identityGeneration) return;

            if (retry.ok) {
              if (rawRetry.conversationId) this.state.conversationId = rawRetry.conversationId;
              this.applyPromptMeta(rawRetry.meta, "response");
              const nextActions = mapServerActionsToWidget(rawRetry.actions);
              const cleanedActions = lead
                ? nextActions?.filter((a) => a.type !== "lead_form")
                : nextActions;
              if (!opts?.suppressReply) {
                this.state.msgs = [
                  ...this.state.msgs,
                  { role: "assistant", content: rawRetry.reply || "", actions: cleanedActions, guidedInputRequested: requestsGuidedInput(rawRetry.meta) },
                ];
              }
              if (postReply) {
                this.state.msgs = [
                  ...this.state.msgs,
                  { role: "assistant", content: postReply },
                ];
              }
              this.pendingFocus = true;
              this.state.busy = false;
              this.endThinkingWindow();
              this.savePersisted(true);
              this.pendingScroll = "lastAssistantStart";
              this.render();
              return;
            }
          }
        } catch {
          // Fall through to friendly network error below.
        }
      }

      if (generation !== this.identityGeneration) return;
      this.state.msgs = [...this.state.msgs, { role: "assistant", content: t.errors.network }];
      this.pendingFocus = true;
      this.pendingScroll = "bottom";
      this.state.busy = false;
      this.endThinkingWindow();
      this.savePersisted(true);
      this.render();
    }
  }
}

function escapeHtml(s: string) {
  return (s || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function linkify(escaped: string) {
  const withoutLeakedAttrs = escaped
    .replace(
      /(?:&quot;|&#039;|["'])\s*target\s*=\s*(?:&quot;|&#039;|["'])[\s\S]*?(?:&quot;|&#039;|["'])\s*rel\s*=\s*(?:&quot;|&#039;|["'])[\s\S]*?(?:&quot;|&#039;|["'])\s*(?:&gt;|>)/gi,
      "",
    )
    .replace(
      /\s*(?:target|rel)\s*=\s*(?:&quot;|&#039;|["'])[^"'<>&]*(?:&quot;|&#039;|["'])/gi,
      "",
    )
    .replace(/(?:&quot;|&#039;)\s*(?=(?:target|rel)\s*=)/gi, "")
    .replace(/(?:noopener|noreferrer)\s*(?:&quot;|&#039;|["'])\s*(?:&gt;|>)/gi, "");

  const normalizedMalformedMarkdown = withoutLeakedAttrs.replace(
    /\[([^\]]{1,160})\]\s*\((https?:\/\/[^)\s"'&]+)\/?(?:&quot;|&#039;|["'])[\s\S]*?\)/gi,
    (_m, rawLabel: string, rawUrl: string) => {
      const label = String(rawLabel || "").trim();
      const url = String(rawUrl || "")
        .replace(/^["']+|["']+$/g, "")
        .trim();
      return label ? `[${label}](${url})` : url;
    },
  );

  // Some model outputs include raw HTML anchors. After escaping, those anchors
  // become text and can leak attributes into the visible message. Normalize
  // them back into plain label+url text before generic URL linkification.
  const normalizedAnchors = normalizedMalformedMarkdown
    .replace(
      /&lt;a\b[\s\S]*?href=(?:&quot;|&#039;)(https?:\/\/[^"'<>\\s]+)(?:&quot;|&#039;)[\s\S]*?&gt;([\s\S]*?)&lt;\/a&gt;/gi,
      (_m, rawUrl: string, rawLabel: string) => {
        const label = String(rawLabel || "")
          .replace(/&nbsp;/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
        return label ? `${label}: ${rawUrl}` : rawUrl;
      },
    )
    // Defensive cleanup for partially escaped HTML attributes leaked into text.
    // These can appear in malformed markdown such as:
    // [Calendario](https://x.com/" target="_blank" rel="noopener noreferrer")
    .replace(
      /\s*(?:target|rel|class|id|style)\s*=\s*(?:(?:&quot;|&#039;)[\s\S]*?(?:&quot;|&#039;)|"[^"]*"|'[^']*')/gi,
      "",
    )
    .replace(
      /(?:&quot;|&#039;)\s*(?=(?:target|rel|class|id|style)\s*=)/gi,
      "",
    )
    .replace(
      /(?:target|rel)\s*=\s*(?:(?:&quot;|&#039;)[^&<>]*(?:&quot;|&#039;)|"[^"]*"|'[^']*')\s*(?=(?:target|rel)\s*=)/gi,
      "",
    )
    .replace(
      /(?:&quot;|&#039;)?\s*target\s*=\s*(?:&quot;|&#039;|")[^"']*(?:&quot;|&#039;|")\s*rel\s*=\s*(?:&quot;|&#039;|")[^"']*(?:&quot;|&#039;|")\s*&gt;/gi,
      "",
    )
    .replace(
      /(?:&quot;|&#039;)?\s*target\s*=\s*(?:&quot;|&#039;|")[^"']*(?:&quot;|&#039;|")\s*rel\s*=\s*(?:&quot;|&#039;|")[^"']*(?:&quot;|&#039;|")/gi,
      "",
    )
    .replace(
      /(?:target|rel)\s*=\s*(?:(?:&quot;|&#039;)[^&<>]*(?:&quot;|&#039;)|"[^"]*"|'[^']*')/gi,
      "",
    )
    .replace(/(?:&quot;|&#039;)\s*&gt;/gi, "")
    .replace(/(?:&quot;|&#039;)\s*(?:noopener|noreferrer)\b/gi, "")
    .replace(/\s*(?:target|rel)\s*=\s*(?:[^\s<]+|"[^"]*"|'[^']*')/gi, "")
    .replace(/\b(?:noopener|noreferrer)\b/gi, "")
    .replace(/[ \t]{2,}/g, " ");

  // Match markdown and bare URLs together so generated anchors are never linkified again.
  return normalizedAnchors
    .replace(/\[([^\]]{1,160})\]\s*\(\s*\)/g, "$1")
    .replace(
      /\[([^\]]{1,160})\]\s*\((https?:\/\/[^)\s]+)\)|(https?:\/\/(?:&amp;|[^\s<"'&])+[^\s<.,:;"')\]"'&])/gi,
      (_m, rawLabel: string | undefined, markdownUrl: string | undefined, bareUrl: string | undefined) => {
        const rawUrl = markdownUrl ?? bareUrl ?? "";
        const cleanUrl = String(rawUrl || "")
          .replace(/^["']+|["']+$/g, "")
          .replace(/\/?["']+$/g, "")
          .trim();
        const cleanLabel = String(rawLabel || "").trim();
        if (!cleanUrl) return cleanLabel;
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanLabel || cleanUrl}</a>`;
      },
    );
}

function productQuoteKey(product: ProductCard) {
  return product.commerce ? `${product.commerce.variantId}:${product.commerce.checkedAt}:${product.commerce.priceMinor}:${product.commerce.currency}` : "";
}

function renderProductCardHtml(
  product: Extract<Action, { type: "product_cards" }>["products"][number],
  locale: "en" | "es",
  hasHandler = false,
  result?: ProductCartResult | { status: "pending" },
  guided = false,
) {
  const fresh = !product.commerce || Date.now() - Date.parse(product.commerce.checkedAt) <= 60_000;
  const compare = product.compareAt && fresh
    ? `<span class="product-compare">${escapeHtml(product.compareAt)}</span>`
    : "";
  const url = safeProductUrl(product.url);
  if (!url) return "";
  const sameOrigin = new URL(url).origin === window.location.origin;
  const canAdd = hasHandler && sameOrigin && product.commerce?.variantId;
  const add = product.discovery && !product.commerce
    ? `<button class="product-cta secondary" type="button" data-select-product="${escapeHtml(product.discovery.productId)}" data-select-catalog="${escapeHtml(product.discovery.catalogId)}">${guided ? (locale === "es" ? "Añadir a selección" : "Add to selection") : (locale === "es" ? "Seleccionar producto" : "Select product")}</button>`
    : canAdd
    ? `<button class="product-cta secondary" type="button" data-cart-product="${escapeHtml(product.commerce!.productId)}" data-cart-quote="${escapeHtml(productQuoteKey(product))}" ${result?.status === "pending" || result?.status === "unknown" ? "disabled" : ""}>${locale === "es" ? "Añadir al carrito" : "Add to cart"}</button>`
    : `<a class="product-cta secondary" href="${escapeHtml(url)}">${locale === "es" ? "Elegir opciones" : "Choose options"}</a>`;
  const status = result ? {
    pending: locale === "es" ? "Añadiendo..." : "Adding...",
    added: locale === "es" ? "Añadido al carrito." : "Added to your cart.",
    failed: locale === "es" ? "No se ha añadido. Comprueba las opciones del producto." : "Not added. Please check the product options.",
    unavailable: locale === "es" ? "Consulta las opciones en la ficha del producto." : "Please use the product page to choose options.",
    unknown: locale === "es" ? "No he podido confirmar el resultado. Revisa el carrito antes de intentarlo de nuevo." : "I could not confirm the result. Check your cart before trying again.",
  }[result.status] : "";
  const cartLink = result && "cartUrl" in result && result.cartUrl ? `<a href="${escapeHtml(result.cartUrl)}">${locale === "es" ? "Ver carrito" : "View cart"}</a>` : "";
  return `<div class="product-card">
    <span class="product-image-wrap">
      ${product.image ? `<img class="product-image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.name)}" loading="lazy" decoding="async" />` : `<span class="product-no-image">${locale === "es" ? "Sin imagen" : "No image"}</span>`}
    </span>
    <span class="product-info">
      <span class="product-name">${escapeHtml(product.name)}</span>
      ${guided && product.discovery ? `<span class="product-ref">${escapeHtml(product.discovery.productId)}</span>` : ""}
      ${product.discovery && !product.price ? "" : `<span class="product-price-row">
        <span class="product-price">${escapeHtml(fresh ? product.price : locale === "es" ? "Consultar precio actual" : "Check current price")}</span>
        ${compare}
      </span>`}
      <span class="product-description">${escapeHtml(product.description)}</span>
      <span class="product-actions">
        <a class="product-cta" href="${escapeHtml(url)}">${guided && product.discovery ? (locale === "es" ? "Ficha oficial" : "Official page") : (locale === "es" ? "Ver producto" : "View product")}</a>
        ${add}
      </span>
      ${status ? `<span class="product-operation" role="status">${escapeHtml(status)} ${cartLink}</span>` : ""}
    </span>
  </div>`;
}

function formatMessageHtmlWithInlineProductCards(
  raw: string,
  products: Extract<Action, { type: "product_cards" }>["products"],
  locale: "en" | "es",
  hasHandler = false,
  results = new Map<string, ProductCartResult | { status: "pending" }>(),
  guided = false,
) {
  const normalized = stripLeakedLinkArtifacts(raw || "");
  const productByHandle = new Map<string, Extract<Action, { type: "product_cards" }>["products"][number]>();
  for (const product of products) {
    if (!product.handle) continue;
    productByHandle.set(product.handle.toLowerCase(), product);
  }

  const markerPattern = /\[PRODUCT:([a-z0-9][a-z0-9-]{0,120})\]/gi;
  let lastIndex = 0;
  let rendered = "";
  let usedMarker = false;
  const usedHandles = new Set<string>();
  const guidedProducts: typeof products = [];
  let match: RegExpExecArray | null;

  while ((match = markerPattern.exec(normalized)) !== null) {
    const textBlock = normalized.slice(lastIndex, match.index).trim();
    if (textBlock && !guided) rendered += formatMessageHtml(textBlock);

    const handle = (match[1] || "").toLowerCase();
    const product = productByHandle.get(handle);
    if (product && !usedHandles.has(handle)) {
      usedMarker = true;
      usedHandles.add(handle);
      if (guided) guidedProducts.push(product);
      else rendered += `<div class="product-cards inline">${renderProductCardHtml(product, locale, hasHandler, product.commerce ? results.get(product.commerce.productId) : undefined)}</div>`;
    }
    lastIndex = markerPattern.lastIndex;
  }

  const tail = normalized.slice(lastIndex).trim();
  if (tail && !guided) rendered += formatMessageHtml(tail);

  if (!usedMarker) {
    const cleaned = normalized.replace(markerPattern, "").trim();
    return formatMessageHtml(cleaned);
  }

  const prose = guided ? formatMessageHtml(normalized.replace(markerPattern, " ")) : rendered;
  return prose + (guidedProducts.length ? `<div class="product-cards inline">${guidedProducts.map(product => renderProductCardHtml(product, locale, hasHandler, product.commerce ? results.get(product.commerce.productId) : undefined, true)).join("")}</div>` : "");
}
function formatMessageHtml(raw: string) {
  const normalizedText = normalizeMessageStructure(stripLeakedLinkArtifacts(raw || ""));
  const escaped = escapeHtml(normalizedText);
  const escapedWithLinks = linkify(escaped);
  const lines = escapedWithLinks.split(/\r?\n/);

  const inline = (s: string) =>
    s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  const joinLines = (arr: string[]) => inline(arr.join("<br/>"));
  const joinParagraphs = (arr: string[]) =>
    arr
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => `<div class="para">${joinLines(part.split(/\r?\n/))}</div>`)
      .join("");

  // ---------- Numbered list ----------
  const firstNumIdx = lines.findIndex((l) => /^\s*\d{1,2}\.\s*/.test(l));
  if (firstNumIdx !== -1) {
    const leadLines = lines.slice(0, firstNumIdx).filter((l) => l.trim() !== "");

    const items: string[] = [];
    const tailLines: string[] = [];

    for (let i = firstNumIdx; i < lines.length; i++) {
      const l = lines[i] ?? "";
      if (/^\s*\d{1,2}\.\s*/.test(l)) {
        const splitItem = splitTrailingWidgetListWrapUp(l.replace(/^\s*\d{1,2}\.\s*/, "").trim());
        items.push(splitItem.item);
        if (splitItem.wrapUp) tailLines.push(splitItem.wrapUp);
        continue;
      }
      if (l.trim() === "") continue;
      tailLines.push(l);
    }

    if (items.length === 0) return joinLines(lines);

    return `
      ${leadLines.length ? `<div class="lead">${joinLines(leadLines)}</div>` : ""}
      <ol class="list">
        ${items.map((it) => `<li>${inline(it)}</li>`).join("")}
      </ol>
      ${tailLines.length ? `<div class="tail">${joinLines(tailLines)}</div>` : ""}
    `;
  }

  // ---------- Bulleted list ----------
  const firstBulIdx = lines.findIndex((l) => /^\s*[-•]\s+/.test(l));
  if (firstBulIdx !== -1) {
    const leadLines = lines.slice(0, firstBulIdx).filter((l) => l.trim() !== "");

    const items: string[] = [];
    const tailLines: string[] = [];

    for (let i = firstBulIdx; i < lines.length; i++) {
      const l = lines[i] ?? "";
      if (/^\s*[-•]\s+/.test(l)) {
        items.push(l.replace(/^\s*[-•]\s+/, "").trim());
        continue;
      }
      if (l.trim() === "") continue;
      tailLines.push(l);
    }

    if (items.length === 0) return joinLines(lines);

    return `
      ${leadLines.length ? `<div class="lead">${joinLines(leadLines)}</div>` : ""}
      <ul class="list">
        ${items.map((it) => `<li>${inline(it)}</li>`).join("")}
      </ul>
      ${tailLines.length ? `<div class="tail">${joinLines(tailLines)}</div>` : ""}
    `;
  }

  // ---------- Fallback ----------
  const paragraphs = escapedWithLinks
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length >= 2) return joinParagraphs(paragraphs);
  // Segment plain text: generated anchor markup must never enter sentence detection.
  const autoParagraphs = autoParagraphizeSingleBlock(normalizedText);
  if (autoParagraphs.length >= 2) return joinParagraphs(autoParagraphs.map(part => linkify(escapeHtml(part))));
  return joinLines(lines);
}


function splitTrailingWidgetListWrapUp(item: string): { item: string; wrapUp: string | null } {
  const match = String(item || "").match(
    /^(.+?[.!?])\s+((?:Once|After|Then|When|If|Una vez|Después|Luego|Cuando|Si)\b.+)$/i,
  );
  if (!match) return { item, wrapUp: null };
  return {
    item: (match[1] || item).trim(),
    wrapUp: (match[2] || "").trim() || null,
  };
}

function autoParagraphizeSingleBlock(text: string): string[] {
  const value = String(text || "").trim();
  if (!value || value.includes("<br/>")) return [];

  const sentences = sentenceSegments(value);
  if (sentences.length < 3) return [];

  const paragraphs: string[] = [];
  let current: string[] = [];
  let currentLen = 0;

  for (const sentence of sentences) {
    const nextLen = currentLen + (current.length > 0 ? 1 : 0) + sentence.length;
    if (current.length >= 2 || (current.length > 0 && nextLen > 180)) {
      paragraphs.push(current.join(" ").trim());
      current = [sentence];
      currentLen = sentence.length;
      continue;
    }
    current.push(sentence);
    currentLen = nextLen;
  }

  if (current.length > 0) paragraphs.push(current.join(" ").trim());
  return paragraphs.filter(Boolean);
}

function normalizeMessageStructure(raw: string): string {
  const value = String(raw || "").trim();
  if (!value) return value;

  return value
    .replace(/:\s*(\d{1,2}\.)\s*(?=(?:\*\*)?[A-Za-zÁÉÍÓÚÜÑáéíóúüñ])/g, ":\n\n$1 ")
    .replace(/\s+(\d{1,2}\.)\s*(?=(?:\*\*)?[A-Za-zÁÉÍÓÚÜÑáéíóúüñ])/g, "\n$1 ")
    .replace(/:\s*\n(\d+\.)/g, ":\n\n$1")
    .replace(/\s+(En AVAST)\b/g, "\n\n$1")
    .replace(/\s+(AVAST define|AVAST presenta|AVAST entiende)\b/g, "\n\n$1")
    .replace(/\s+(Es importante destacar)\b/g, "\n\n$1")
    .replace(/\s+(Es importante tener en cuenta)\b/g, "\n\n$1")
    .replace(/\s+(Además)\b/g, "\n\n$1")
    .replace(/\s+(Por otro lado)\b/g, "\n\n$1")
    .replace(/\s+(Si deseas)\b/g, "\n\n$1")
    .replace(/\s+(Si quieres)\b/g, "\n\n$1")
    .replace(/\s+(Si lo prefieres)\b/g, "\n\n$1")
    .replace(/\s+(Aquí tienes el enlace)\b/g, "\n\n$1")
    .replace(/\s+(In AVAST)\b/g, "\n\n$1")
    .replace(/\s+(It(?:'|’)s important to note)\b/g, "\n\n$1")
    .replace(/\s+(If you want)\b/g, "\n\n$1")
    .replace(/\s+(If you'd like)\b/g, "\n\n$1")
    .replace(/\s+(If you would like)\b/g, "\n\n$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function splitBodyAndSource(raw: string, keepEmptyBodySource = false): { body: string; source: string | null } {
  const text = (raw || "").trim();
  if (!text) return { body: "", source: null };

  const lines = text.split(/\r?\n/);
  let i = lines.length - 1;
  while (i >= 0 && lines[i].trim() === "") i -= 1;
  if (i < 0) return { body: "", source: null };

  const tail = lines[i].trim();
  if (!/^(source|fuente)\s*:/i.test(tail)) {
    return { body: text, source: null };
  }

  const source = tail;
  const body = lines.slice(0, i).join("\n").trim();
  return { body: body || (keepEmptyBodySource ? text : ""), source };
}

if (!customElements.get("aliigo-widget")) {
  try {
    customElements.define("aliigo-widget", AliigoWidget);
  } catch (err) {
    console.error("[aliigo-widget] customElements.define failed", err);
  }
}
